import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { downloadTemplatePDF } from "./utils/pdfGenerator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { 
  FileText, FileSpreadsheet, ClipboardList, Calculator, Bug, TestTube, Code, 
  Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Settings, Check, Star, Edit, Building2,
  Target, TrendingUp, Users, DollarSign, Briefcase, Wrench, BarChart3, PieChart,
  AlertTriangle, MessageSquare, Calendar, Layers, GitBranch, Shield, Server, BookOpen, Save, Clock,
  FileUp, FileDown
} from "lucide-react";

// Import template forms
import {
  RACIMatrixForm,
  StrategicRoadmapForm,
  FinancialForecastForm,
  ROIAnalysisForm,
  VendorComparisonForm,
  CapacityPlanningForm,
  ResourceAllocationForm,
  HiringPlanForm,
  SkillsMatrixForm,
  ReleaseNotesForm,
  IncidentReportForm,
  PostMortemForm,
  RunbookForm,
  APIDocumentationForm,
  BoardReportForm,
  QBRForm,
  InvestorUpdateForm,
  SoftwareProposalForm,
  ProductTrackSheetForm,
  CostCalculationForm,
  BugLogForm,
  TestCasesForm,
  CodeReviewForm,
  StakeholderCommunicationLogForm,
  ProductBacklogForm,
  BurndownChartForm,
  DefinitionOfDoneForm,
  DefinitionOfReadyForm,
  DecisionLogForm,
  IssueRiskRegisterForm,
  EscalationMatrixForm,
  ProjectClosureForm,
  LessonsLearnedForm,
  ClientFeedbackForm
} from "./templates";

// Use relative URLs for production deployment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ==================== DCTM FILE UTILITIES ====================
const DCTM_FILE_VERSION = "1.0";

const exportToDCTM = (formData, templateId, templateType, createdBy = '', fileName = '') => {
  const dctmDocument = {
    _dctm: {
      version: DCTM_FILE_VERSION,
      format: "dctm",
      generator: "Document Templates",
      exportedAt: new Date().toISOString()
    },
    meta: {
      templateId,
      templateType,
      createdBy,
      documentVersion: formData.version || "1.0",
      name: formData.project_name || formData.title || formData.meeting_title || formData.company_name || `${templateType} Document`
    },
    data: formData
  };

  const jsonString = JSON.stringify(dctmDocument, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const docName = fileName || dctmDocument.meta.name.replace(/[^a-zA-Z0-9]/g, '_');
  const finalFileName = `${docName}_v${dctmDocument.meta.documentVersion}.dctm`;
  
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};

const importFromDCTM = (file) => {
  return new Promise((resolve) => {
    if (!file) {
      resolve({ success: false, error: 'No file provided' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.dctm')) {
      resolve({ success: false, error: 'Invalid file format. Please select a .dctm file.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const parsed = JSON.parse(content);
        if (!parsed._dctm || !parsed.meta || !parsed.data) {
          resolve({ success: false, error: 'Invalid .dctm file structure.' });
          return;
        }
        resolve({ success: true, data: parsed.data, meta: parsed.meta, dctmInfo: parsed._dctm });
      } catch (err) {
        resolve({ success: false, error: 'Failed to parse .dctm file.' });
      }
    };
    reader.onerror = () => resolve({ success: false, error: 'Failed to read the file.' });
    reader.readAsText(file);
  });
};

const validateDCTMTemplate = (expectedTemplateId, importedMeta) => {
  if (!importedMeta || !importedMeta.templateId) {
    return { valid: false, warning: 'Missing template information in file.' };
  }
  if (importedMeta.templateId !== expectedTemplateId) {
    return { valid: true, warning: `This file was created for "${importedMeta.templateType}" template. Some fields may not match.` };
  }
  return { valid: true };
};

// ==================== REGIONAL SETTINGS OPTIONS ====================
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR - Euro (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP - British Pound (£)', symbol: '£' },
  { value: 'INR', label: 'INR - Indian Rupee (₹)', symbol: '₹' },
  { value: 'JPY', label: 'JPY - Japanese Yen (¥)', symbol: '¥' },
  { value: 'AUD', label: 'AUD - Australian Dollar (A$)', symbol: 'A$' },
  { value: 'CAD', label: 'CAD - Canadian Dollar (C$)', symbol: 'C$' },
  { value: 'CHF', label: 'CHF - Swiss Franc (Fr)', symbol: 'Fr' },
  { value: 'CNY', label: 'CNY - Chinese Yuan (¥)', symbol: '¥' },
  { value: 'SGD', label: 'SGD - Singapore Dollar (S$)', symbol: 'S$' },
  { value: 'AED', label: 'AED - UAE Dirham (د.إ)', symbol: 'د.إ' },
];

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC +00:00' },
  { value: 'America/New_York', label: 'America/New_York -05:00 (EST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles -08:00 (PST)' },
  { value: 'America/Chicago', label: 'America/Chicago -06:00 (CST)' },
  { value: 'Europe/London', label: 'Europe/London +00:00 (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris +01:00 (CET)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin +01:00 (CET)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata +05:30 (IST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai +04:00 (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore +08:00 (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo +09:00 (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai +08:00 (CST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney +11:00 (AEDT)' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2026)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-12-31)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2026)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2026)' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' },
];

// ==================== AUTH CONTEXT ====================
import React, { createContext, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Key, Building, UserCheck, RefreshCw, Copy, Eye, EyeOff, LogOut, Crown } from "lucide-react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check for token in URL or localStorage
    const urlToken = searchParams.get('token');
    const storedToken = localStorage.getItem('auth_token');
    const token = urlToken || storedToken;

    if (token) {
      verifySession(token);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const verifySession = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/session?token=${token}`);
      if (response.data.authenticated) {
        setUser(response.data);
        localStorage.setItem('auth_token', token);
        // Clean URL if token was in params
        if (searchParams.get('token')) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      } else {
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error("Session verification failed:", error);
      localStorage.removeItem('auth_token');
    }
    setLoading(false);
  };

  const logout = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        await axios.post(`${API}/auth/logout?token=${token}`);
      } catch (error) {
        console.error("Logout error:", error);
      }
    }
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, verifySession }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== LOGIN PAGE ====================
const TURNSTILE_SITE_KEY = "0x4AAAAAACOhFwth0SiXecYv";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // email | code
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const { verifySession } = useAuth();
  const turnstileRef = useRef(null);

  useEffect(() => {
    // Load Turnstile script
    if (!document.querySelector('script[src*="turnstile"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    // Render Turnstile widget when on email step
    if (step === 'email' && turnstileRef.current) {
      // Function to render widget
      const renderWidget = () => {
        if (!window.turnstile || !turnstileRef.current) return;
        
        // Clear the container first
        turnstileRef.current.innerHTML = '';
        
        // Render new widget
        try {
          const widgetId = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => setTurnstileToken(token),
            'error-callback': () => setTurnstileToken(""),
            'expired-callback': () => setTurnstileToken("")
          });
          setTurnstileWidgetId(widgetId);
        } catch (e) {
          console.log('Turnstile render error:', e);
        }
      };
      
      // If turnstile is loaded, render immediately, otherwise wait
      if (window.turnstile) {
        renderWidget();
      } else {
        // Poll for turnstile to load
        const checkInterval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkInterval);
            renderWidget();
          }
        }, 100);
        // Clean up interval after 10 seconds
        setTimeout(() => clearInterval(checkInterval), 10000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleUseDifferentEmail = () => {
    // Clear all state
    setCode("");
    setError("");
    setTurnstileToken("");
    // Remove existing turnstile widget
    if (turnstileWidgetId !== null && window.turnstile) {
      try {
        window.turnstile.remove(turnstileWidgetId);
      } catch (e) {}
      setTurnstileWidgetId(null);
    }
    if (turnstileRef.current) {
      turnstileRef.current.innerHTML = '';
    }
    // Change step - a small delay ensures state is cleared before Turnstile re-renders
    setTimeout(() => {
      setStep("email");
    }, 100);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/request-access`, { 
        email,
        turnstileToken 
      });
      if (response.data.success) {
        setStep("code");
        // If code is returned (email failed), pre-fill it
        if (response.data.code) {
          setCode(response.data.code);
        }
        // Show note if email wasn't sent
        if (response.data.note) {
          setError(response.data.note);
        }
      } else {
        setError(response.data.error || "Failed to send access code");
        // Reset Turnstile
        if (window.turnstile && turnstileRef.current) {
          window.turnstile.reset(turnstileRef.current);
        }
      }
    } catch (error) {
      setError("Failed to send access code. Please try again.");
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/verify-code`, { email, code });
      if (response.data.success) {
        localStorage.setItem('auth_token', response.data.token);
        await verifySession(response.data.token);
      } else {
        setError(response.data.error || "Invalid code");
      }
    } catch (error) {
      setError("Verification failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Request Access</CardTitle>
          <CardDescription>
            {step === "email" 
              ? "Enter your email to receive an access code" 
              : "Enter the access code sent to your email"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "email" ? (
            <form onSubmit={handleRequestAccess} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              {/* Cloudflare Turnstile CAPTCHA */}
              <div ref={turnstileRef} className="flex justify-center"></div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="send-code-btn">
                {loading ? "Sending..." : "Send Access Key"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <Label htmlFor="code">Access Code</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="code"
                    type="text"
                    placeholder="Enter access code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="pl-10 font-mono text-sm"
                    required
                    data-testid="login-code-input"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">Code sent to {email}</p>
              </div>
              {error && (
                <div className={`text-sm p-2 rounded ${error.includes('Code shown') || error.includes('Email delivery') ? 'bg-amber-50 text-amber-700' : 'text-red-500'}`}>
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={loading} data-testid="verify-code-btn">
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleUseDifferentEmail}>
                Use different email
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== SUPER ADMIN PANEL ====================
const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("domains");
  
  // New domain form
  const [newDomain, setNewDomain] = useState({ domain: "", company_name: "", admin_email: "" });
  const [newEmail, setNewEmail] = useState({ email: "", name: "" });
  const [showApiKey, setShowApiKey] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('auth_token');
    try {
      const [domainsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/admin/domains?token=${token}`),
        axios.get(`${API}/admin/emails?token=${token}`)
      ]);
      setDomains(domainsRes.data.error ? [] : domainsRes.data);
      setEmails(emailsRes.data.error ? [] : emailsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    }
    setLoading(false);
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.post(`${API}/admin/domains?token=${token}`, newDomain);
      if (response.data.success) {
        setNewDomain({ domain: "", company_name: "", admin_email: "" });
        fetchData();
      } else {
        alert(response.data.error || "Failed to add domain");
      }
    } catch (error) {
      alert("Error adding domain");
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm("Are you sure you want to delete this domain?")) return;
    const token = localStorage.getItem('auth_token');
    try {
      await axios.delete(`${API}/admin/domains/${domainId}?token=${token}`);
      fetchData();
    } catch (error) {
      alert("Error deleting domain");
    }
  };

  const handleToggleApiAccess = async (domainId, currentValue) => {
    const token = localStorage.getItem('auth_token');
    try {
      await axios.put(`${API}/admin/domains/${domainId}?token=${token}`, { api_access_enabled: !currentValue });
      fetchData();
    } catch (error) {
      alert("Error updating domain");
    }
  };

  const handleRegenerateKey = async (domainId) => {
    if (!window.confirm("This will invalidate the current API key. Continue?")) return;
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.post(`${API}/admin/domains/${domainId}/regenerate-key?token=${token}`);
      if (response.data.success) {
        fetchData();
        alert("API key regenerated successfully");
      }
    } catch (error) {
      alert("Error regenerating key");
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.post(`${API}/admin/emails?token=${token}`, newEmail);
      if (response.data.success) {
        setNewEmail({ email: "", name: "" });
        fetchData();
      } else {
        alert(response.data.error || "Failed to add email");
      }
    } catch (error) {
      alert("Error adding email");
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm("Are you sure you want to delete this email?")) return;
    const token = localStorage.getItem('auth_token');
    try {
      await axios.delete(`${API}/admin/emails/${emailId}?token=${token}`);
      fetchData();
    } catch (error) {
      alert("Error deleting email");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  if (!user?.is_super_admin) {
    return <div className="p-8 text-center text-red-500">Access Denied - Super Admin Only</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Crown className="h-6 w-6 text-yellow-500" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Super Admin Panel</h1>
                  <p className="text-sm text-gray-500">Manage domains, users, and API access</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="domains">Domains ({domains.length})</TabsTrigger>
            <TabsTrigger value="emails">Individual Emails ({emails.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="domains">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Add New Domain</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddDomain} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Domain</Label>
                    <Input 
                      placeholder="company.com" 
                      value={newDomain.domain} 
                      onChange={(e) => setNewDomain({...newDomain, domain: e.target.value})}
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Company Name</Label>
                    <Input 
                      placeholder="Company Inc." 
                      value={newDomain.company_name} 
                      onChange={(e) => setNewDomain({...newDomain, company_name: e.target.value})}
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Admin Email</Label>
                    <Input 
                      placeholder="admin@company.com" 
                      value={newDomain.admin_email} 
                      onChange={(e) => setNewDomain({...newDomain, admin_email: e.target.value})}
                    />
                  </div>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-2" /> Add Domain
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {domains.map((domain) => (
                <Card key={domain.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Building className="h-5 w-5 text-blue-500" />
                          <h3 className="font-bold text-lg">{domain.domain}</h3>
                          {domain.api_access_enabled ? (
                            <Badge className="bg-green-100 text-green-800">API Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">API Disabled</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{domain.company_name || "No company name"}</p>
                        <p className="text-sm text-gray-500">Admin: {domain.admin_email || "Not set"}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                            {showApiKey[domain.id] ? domain.api_key : "••••••••••••••••"}
                          </code>
                          <Button variant="ghost" size="sm" onClick={() => setShowApiKey({...showApiKey, [domain.id]: !showApiKey[domain.id]})}>
                            {showApiKey[domain.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(domain.api_key)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleToggleApiAccess(domain.id, domain.api_access_enabled)}>
                          {domain.api_access_enabled ? "Disable API" : "Enable API"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleRegenerateKey(domain.id)}>
                          <RefreshCw className="h-4 w-4 mr-1" /> Regenerate Key
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteDomain(domain.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="emails">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Add Individual Email</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddEmail} className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label>Email</Label>
                    <Input 
                      type="email"
                      placeholder="user@example.com" 
                      value={newEmail.email} 
                      onChange={(e) => setNewEmail({...newEmail, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <Label>Name</Label>
                    <Input 
                      placeholder="John Doe" 
                      value={newEmail.name} 
                      onChange={(e) => setNewEmail({...newEmail, name: e.target.value})}
                    />
                  </div>
                  <Button type="submit">
                    <Plus className="h-4 w-4 mr-2" /> Add Email
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {emails.map((emailRecord) => (
                <Card key={emailRecord.id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <UserCheck className="h-5 w-5 text-green-500" />
                        <div>
                          <h3 className="font-medium">{emailRecord.email}</h3>
                          <p className="text-sm text-gray-500">{emailRecord.name || "No name"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                          {showApiKey[emailRecord.id] ? emailRecord.api_key : "••••••••••••••••"}
                        </code>
                        <Button variant="ghost" size="sm" onClick={() => setShowApiKey({...showApiKey, [emailRecord.id]: !showApiKey[emailRecord.id]})}>
                          {showApiKey[emailRecord.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(emailRecord.api_key)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteEmail(emailRecord.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ==================== SETTINGS CONTEXT ====================
const SettingsContext = createContext(null);

export const useSettings = () => useContext(SettingsContext);

const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState([]);
  const [activeSettings, setActiveSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.email) {
      fetchSettings();
    }
  }, [user?.email]);

  const fetchSettings = async () => {
    const headers = user?.email ? { 'X-User-Email': user.email } : {};
    try {
      const [allSettings, defaultSetting] = await Promise.all([
        axios.get(`${API}/settings`, { headers }),
        axios.get(`${API}/settings/default/current`, { headers })
      ]);
      setSettings(allSettings.data);
      setActiveSettings(defaultSetting.data?.id ? defaultSetting.data : null);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
    setLoading(false);
  };

  return (
    <SettingsContext.Provider value={{ settings, activeSettings, fetchSettings, loading, userEmail: user?.email }}>
      {children}
    </SettingsContext.Provider>
  );
};

// ==================== DASHBOARD ====================
const Dashboard = () => {
  const { activeSettings } = useSettings();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Original templates
  const existingTemplates = [
    { id: "ba-requirements", title: "BA Requirements Document", description: "Standard format for Business Analysts to document client requirements", icon: FileText, color: "bg-blue-500", format: "DOCX" },
    { id: "software-proposal", title: "Software Development Proposal", description: "Comprehensive proposal template for software implementation projects", icon: FileText, color: "bg-purple-500", format: "DOCX" },
    { id: "product-tracksheet", title: "Product Map / Track Sheet", description: "Track different stages and phases of software development", icon: ClipboardList, color: "bg-green-500", format: "XLSX" },
    { id: "cost-calculation", title: "Cost Calculation Sheet", description: "Calculate skill-wise bandwidth, man hours, API costs, tools & technology costs", icon: Calculator, color: "bg-orange-500", format: "XLSX" },
    { id: "bug-log", title: "Bug Fixing Log Sheet", description: "Track and manage bug fixes throughout the development lifecycle", icon: Bug, color: "bg-red-500", format: "XLSX" },
    { id: "test-cases", title: "Test Cases Document", description: "Standard format for documenting test scenarios and results", icon: TestTube, color: "bg-teal-500", format: "XLSX" },
    { id: "code-review", title: "Code Review Checklist", description: "Comprehensive checklist for code review process", icon: Code, color: "bg-indigo-500", format: "XLSX" }
  ];

  // Meetings & Communication templates
  const meetingsTemplates = [
    { id: "minutes-of-meeting", title: "Minutes of Meeting (MoM)", description: "Document meeting discussions, decisions, and action items", icon: FileText, color: "bg-cyan-500", format: "DOCX" },
    { id: "meeting-agenda", title: "Meeting Agenda", description: "Plan and organize meeting topics and objectives", icon: FileText, color: "bg-cyan-600", format: "DOCX" },
    { id: "action-item-tracker", title: "Action Item Tracker", description: "Track and manage action items with status and deadlines", icon: ClipboardList, color: "bg-cyan-700", format: "XLSX" },
    { id: "stakeholder-communication-log", title: "Stakeholder Communication Log", description: "Log all stakeholder communications and follow-ups", icon: FileSpreadsheet, color: "bg-cyan-800", format: "XLSX" }
  ];

  // Scrum / Agile templates
  const scrumTemplates = [
    { id: "sprint-planning", title: "Sprint Planning Sheet", description: "Plan sprint capacity, user stories, and team allocation", icon: ClipboardList, color: "bg-emerald-500", format: "XLSX" },
    { id: "daily-standup", title: "Daily Standup Tracker", description: "Track daily standup updates and blockers", icon: FileSpreadsheet, color: "bg-emerald-600", format: "XLSX" },
    { id: "sprint-retrospective", title: "Sprint Retrospective", description: "Document what went well and areas for improvement", icon: FileText, color: "bg-emerald-700", format: "DOCX" },
    { id: "product-backlog", title: "Product Backlog", description: "Manage and prioritize product backlog items", icon: ClipboardList, color: "bg-emerald-800", format: "XLSX" },
    { id: "burndown-chart", title: "Burndown Chart / Velocity Tracker", description: "Track sprint progress and team velocity", icon: FileSpreadsheet, color: "bg-lime-500", format: "XLSX" },
    { id: "definition-of-done", title: "Definition of Done (DoD)", description: "Define completion criteria for work items", icon: FileText, color: "bg-lime-600", format: "DOCX" },
    { id: "definition-of-ready", title: "Definition of Ready (DoR)", description: "Define readiness criteria for backlog items", icon: FileText, color: "bg-lime-700", format: "DOCX" }
  ];

  // Project Governance templates
  const governanceTemplates = [
    { id: "change-request", title: "Change Request Form", description: "Document and track project change requests", icon: FileText, color: "bg-amber-500", format: "DOCX" },
    { id: "decision-log", title: "Decision Log", description: "Track project decisions and their rationale", icon: FileSpreadsheet, color: "bg-amber-600", format: "XLSX" },
    { id: "issue-risk-register", title: "Issue Log / Risk Register", description: "Track issues and risks with mitigation plans", icon: ClipboardList, color: "bg-amber-700", format: "XLSX" },
    { id: "escalation-matrix", title: "Escalation Matrix", description: "Define escalation levels and procedures", icon: FileText, color: "bg-amber-800", format: "DOCX" }
  ];

  // Delivery & Closure templates
  const deliveryTemplates = [
    { id: "go-live-checklist", title: "Go-Live Checklist", description: "Pre-deployment checklist for go-live readiness", icon: FileText, color: "bg-rose-500", format: "DOCX" },
    { id: "project-closure", title: "Project Closure Report", description: "Document project completion and lessons learned", icon: FileText, color: "bg-rose-600", format: "DOCX" },
    { id: "lessons-learned", title: "Lessons Learned", description: "Capture insights for future project improvement", icon: FileText, color: "bg-rose-700", format: "DOCX" },
    { id: "client-feedback", title: "Client Feedback Form", description: "Collect and document client satisfaction feedback", icon: FileSpreadsheet, color: "bg-rose-800", format: "XLSX" }
  ];

  // Strategy & OKRs templates
  const strategyTemplates = [
    { id: "okr", title: "OKR Template", description: "Objectives & Key Results tracking with RAI assignments", icon: Target, color: "bg-violet-500", format: "XLSX" },
    { id: "kpi-dashboard", title: "KPI Dashboard", description: "Key Performance Indicators tracking and analysis", icon: TrendingUp, color: "bg-violet-600", format: "XLSX" },
    { id: "raci-matrix", title: "RACI Matrix", description: "Responsibility assignment matrix for projects", icon: Users, color: "bg-violet-700", format: "XLSX" },
    { id: "strategic-roadmap", title: "Strategic Roadmap", description: "Long-term planning, milestones, and initiatives", icon: Layers, color: "bg-violet-800", format: "XLSX" }
  ];

  // Finance & Budgeting templates
  const financeTemplates = [
    { id: "budget-planning", title: "Budget Planning Sheet", description: "Annual/quarterly budget allocation and tracking", icon: DollarSign, color: "bg-green-600", format: "XLSX" },
    { id: "financial-forecast", title: "Financial Forecast", description: "Revenue & expense projections with scenarios", icon: TrendingUp, color: "bg-green-700", format: "XLSX" },
    { id: "roi-analysis", title: "ROI Analysis", description: "Return on investment calculations and analysis", icon: PieChart, color: "bg-green-800", format: "XLSX" },
    { id: "vendor-comparison", title: "Vendor Comparison", description: "Vendor evaluation and selection matrix", icon: BarChart3, color: "bg-green-900", format: "XLSX" }
  ];

  // Resource & Capacity templates
  const resourceTemplates = [
    { id: "capacity-planning", title: "Capacity Planning", description: "Team capacity & allocation planning", icon: Users, color: "bg-sky-500", format: "XLSX" },
    { id: "resource-allocation", title: "Resource Allocation Matrix", description: "Resource distribution across projects", icon: Layers, color: "bg-sky-600", format: "XLSX" },
    { id: "hiring-plan", title: "Hiring Plan", description: "Recruitment planning and tracking", icon: Briefcase, color: "bg-sky-700", format: "XLSX" },
    { id: "skills-matrix", title: "Skills Matrix", description: "Team skills assessment and gap analysis", icon: Star, color: "bg-sky-800", format: "XLSX" }
  ];

  // Engineering & DevOps templates
  const engineeringTemplates = [
    { id: "technical-design", title: "Technical Design Document", description: "Architecture & design specifications with RAI", icon: GitBranch, color: "bg-slate-600", format: "DOCX" },
    { id: "release-notes", title: "Release Notes", description: "Version release documentation", icon: FileText, color: "bg-slate-700", format: "DOCX" },
    { id: "incident-report", title: "Incident Report", description: "Production incident documentation", icon: AlertTriangle, color: "bg-red-600", format: "DOCX" },
    { id: "post-mortem", title: "Post-Mortem / RCA", description: "Root cause analysis with Five Whys", icon: Shield, color: "bg-red-700", format: "DOCX" },
    { id: "runbook", title: "Infrastructure Runbook", description: "Operations procedures and playbooks", icon: Server, color: "bg-slate-800", format: "DOCX" },
    { id: "api-documentation", title: "API Documentation", description: "API specs, endpoints, and examples", icon: BookOpen, color: "bg-slate-900", format: "DOCX" }
  ];

  // Executive Reports templates
  const executiveTemplates = [
    { id: "executive-summary", title: "Executive Summary", description: "High-level project/company status report", icon: FileText, color: "bg-indigo-600", format: "DOCX" },
    { id: "board-report", title: "Board Report", description: "Board meeting presentation preparation", icon: Briefcase, color: "bg-indigo-700", format: "DOCX" },
    { id: "qbr", title: "Quarterly Business Review", description: "QBR metrics, analysis, and insights", icon: BarChart3, color: "bg-indigo-800", format: "XLSX" },
    { id: "investor-update", title: "Investor Update", description: "Investor communication and metrics", icon: TrendingUp, color: "bg-indigo-900", format: "DOCX" }
  ];

  const renderTemplateCards = (templates) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {templates.map((template) => (
        <Link key={template.id} to={`/template/${template.id}`} data-testid={`template-card-${template.id}`}>
          <Card className="h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer border-2 border-transparent hover:border-blue-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className={`p-3 ${template.color} rounded-lg`}>
                  <template.icon className="h-6 w-6 text-white" />
                </div>
                <Badge variant="outline" className="font-mono">{template.format}</Badge>
              </div>
              <CardTitle className="text-lg mt-4">{template.title}</CardTitle>
              <CardDescription>{template.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full">Create Document</Button>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg">
                <FileText className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Document Templates</h1>
                <p className="text-gray-500">Professional templates for professionals</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user?.is_super_admin && (
                <Link to="/admin">
                  <Button variant="outline" className="gap-2 border-yellow-300 bg-yellow-50 hover:bg-yellow-100">
                    <Crown className="h-4 w-4 text-yellow-600" />
                    Admin
                  </Button>
                </Link>
              )}
              <Link to="/saved-templates">
                <Button variant="outline" className="gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Draft
                </Button>
              </Link>
              <Link to="/docs">
                <Button variant="outline" className="gap-2">
                  <BookOpen className="h-4 w-4" />
                  API Docs
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="outline" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Company Settings
                </Button>
              </Link>
              {user && (
                <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                  <span className="text-sm text-gray-600">{user.email}</span>
                  <Button variant="ghost" size="sm" onClick={logout}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {activeSettings && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
            <Building2 className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-800">
              Active Template: <strong>{activeSettings.name}</strong>
              {activeSettings.company_name && ` - ${activeSettings.company_name}`}
            </span>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Strategy & OKRs */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-violet-500 rounded-full"></div>
            Strategy & OKRs
          </h2>
          {renderTemplateCards(strategyTemplates)}
        </section>

        {/* Finance & Budgeting */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-green-600 rounded-full"></div>
            Finance & Budgeting
          </h2>
          {renderTemplateCards(financeTemplates)}
        </section>

        {/* Resource & Capacity */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-sky-500 rounded-full"></div>
            Resource & Capacity
          </h2>
          {renderTemplateCards(resourceTemplates)}
        </section>

        {/* Executive Reports */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-indigo-600 rounded-full"></div>
            Executive Reports (CxO)
          </h2>
          {renderTemplateCards(executiveTemplates)}
        </section>

        {/* Engineering & DevOps */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-slate-600 rounded-full"></div>
            Engineering & DevOps
          </h2>
          {renderTemplateCards(engineeringTemplates)}
        </section>

        {/* Existing Templates */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
            Core Development Templates
          </h2>
          {renderTemplateCards(existingTemplates)}
        </section>

        {/* Meetings & Communication */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-cyan-500 rounded-full"></div>
            Meetings & Communication
          </h2>
          {renderTemplateCards(meetingsTemplates)}
        </section>

        {/* Scrum / Agile */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
            Scrum / Agile
          </h2>
          {renderTemplateCards(scrumTemplates)}
        </section>

        {/* Project Governance */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
            Project Governance
          </h2>
          {renderTemplateCards(governanceTemplates)}
        </section>

        {/* Delivery & Closure */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-1 h-6 bg-rose-500 rounded-full"></div>
            Delivery & Closure
          </h2>
          {renderTemplateCards(deliveryTemplates)}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-gray-600">
              <FileText className="h-5 w-5" />
              <span className="font-medium">Document Templates</span>
              <Badge variant="outline" className="ml-2">v2.1.2</Badge>
            </div>
            <div className="text-center text-sm text-gray-500">
              A product of{" "}
              <a href="https://dnsstack.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                DNS Stack
              </a>
            </div>
            <div className="text-sm text-gray-500">
              © 2026 Document Templates. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== SETTINGS PAGE ====================
const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings, fetchSettings } = useSettings();
  const [editingSetting, setEditingSetting] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const fileInputRef = React.useRef(null);
  const headers = user?.email ? { 'X-User-Email': user.email } : {};
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    company_name: "",
    tagline: "",
    company_email: "",
    company_phone: "",
    company_website: "",
    company_address: "",
    currency: "USD",
    timezone: "UTC",
    date_format: "MMM DD, YYYY",
    time_format: "12h",
    header_line1: "",
    header_line2: "",
    footer_line1: "",
    footer_line2: "",
    is_default: false
  });

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      company_name: "",
      tagline: "",
      company_email: "",
      company_phone: "",
      company_website: "",
      company_address: "",
      currency: "USD",
      timezone: "UTC",
      date_format: "MMM DD, YYYY",
      time_format: "12h",
      header_line1: "",
      header_line2: "",
      footer_line1: "",
      footer_line2: "",
      is_default: false
    });
    setEditingSetting(null);
    setLogoPreview('');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData({ ...formData, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      if (editingSetting) {
        await axios.put(`${API}/settings/${editingSetting}`, formData, { headers });
      } else {
        await axios.post(`${API}/settings`, formData, { headers });
      }
      fetchSettings();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings");
    }
  };

  const handleEdit = (setting) => {
    setFormData({
      name: setting.name || "",
      logo_url: setting.logo_url || "",
      company_name: setting.company_name || "",
      tagline: setting.tagline || "",
      company_email: setting.company_email || "",
      company_phone: setting.company_phone || "",
      company_website: setting.company_website || "",
      company_address: setting.company_address || "",
      currency: setting.currency || "USD",
      timezone: setting.timezone || "UTC",
      date_format: setting.date_format || "MMM DD, YYYY",
      time_format: setting.time_format || "12h",
      header_line1: setting.header_line1 || "",
      header_line2: setting.header_line2 || "",
      footer_line1: setting.footer_line1 || "",
      footer_line2: setting.footer_line2 || "",
      is_default: setting.is_default || false
    });
    setLogoPreview(setting.logo_url || '');
    setEditingSetting(setting.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this setting?")) return;
    try {
      await axios.delete(`${API}/settings/${id}`, { headers });
      fetchSettings();
    } catch (error) {
      console.error("Error deleting setting:", error);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.post(`${API}/settings/${id}/set-default`, {}, { headers });
      fetchSettings();
    } catch (error) {
      console.error("Error setting default:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Company Settings</h1>
                <p className="text-sm text-gray-500">Configure document branding with logo and company information</p>
              </div>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingSetting ? "Edit Template" : "Create New Template"}</DialogTitle>
                  <DialogDescription>Configure header, footer, logo and company details for your documents</DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Template Name */}
                  <div>
                    <Label>Template Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., VROZART" className="mt-1" />
                  </div>
                  
                  <Separator />
                  
                  {/* Company Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Building2 className="h-4 w-4" />
                      Company Information
                    </div>
                    
                    {/* Logo Upload */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <Label className="text-sm">Company Logo</Label>
                      <div className="flex items-center gap-4 mt-2">
                        {logoPreview ? (
                          <div className="relative">
                            <img src={logoPreview} alt="Logo" className="h-16 max-w-[200px] object-contain" />
                            <button 
                              type="button" 
                              onClick={() => { setLogoPreview(''); setFormData({...formData, logo_url: ''}); }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="h-16 w-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs">
                            No Logo
                          </div>
                        )}
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            {logoPreview ? 'Change Logo' : 'Upload Logo'}
                          </Button>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPEG, GIF or WebP. Max 2MB.</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Company Name</Label>
                        <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Vrozart Group" className="mt-1" />
                      </div>
                      <div>
                        <Label>Tagline</Label>
                        <Input value={formData.tagline} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} placeholder="Driving growth with purpose" className="mt-1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input value={formData.company_email} onChange={(e) => setFormData({ ...formData, company_email: e.target.value })} placeholder="info@company.com" className="mt-1" />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={formData.company_phone} onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })} placeholder="+91 88008 45946" className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input value={formData.company_website} onChange={(e) => setFormData({ ...formData, company_website: e.target.value })} placeholder="www.company.com" className="mt-1" />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Textarea value={formData.company_address} onChange={(e) => setFormData({ ...formData, company_address: e.target.value })} placeholder="123 Business St, City, Country" rows={2} className="mt-1" />
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Regional Settings */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Regional Settings</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Currency</Label>
                        <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CURRENCY_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Timezone</Label>
                        <Select value={formData.timezone} onValueChange={(value) => setFormData({...formData, timezone: value})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIMEZONE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date Format</Label>
                        <Select value={formData.date_format} onValueChange={(value) => setFormData({...formData, date_format: value})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DATE_FORMAT_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Time Format</Label>
                        <Select value={formData.time_format} onValueChange={(value) => setFormData({...formData, time_format: value})}>
                          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIME_FORMAT_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Document Header & Footer */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Document Header</h4>
                    <div>
                      <Label>Header Line 1</Label>
                      <Input value={formData.header_line1} onChange={(e) => setFormData({ ...formData, header_line1: e.target.value })} placeholder="Company name or main header text" className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">Primary header text (e.g., company name)</p>
                    </div>
                    <div>
                      <Label>Header Line 2</Label>
                      <Input value={formData.header_line2} onChange={(e) => setFormData({ ...formData, header_line2: e.target.value })} placeholder="Tagline or secondary header text" className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">Secondary header text (e.g., tagline, department)</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Document Footer</h4>
                    <div>
                      <Label>Footer Line 1</Label>
                      <Input value={formData.footer_line1} onChange={(e) => setFormData({ ...formData, footer_line1: e.target.value })} placeholder="Address | Email | Phone" className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">Primary footer text (e.g., address, contact info)</p>
                    </div>
                    <div>
                      <Label>Footer Line 2</Label>
                      <Input value={formData.footer_line2} onChange={(e) => setFormData({ ...formData, footer_line2: e.target.value })} placeholder="Confidential, all rights reserved © 2026" className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">Secondary footer text (e.g., copyright, disclaimer)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2">
                    <Switch checked={formData.is_default} onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })} />
                    <Label>Set as default template</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>Cancel</Button>
                  <Button onClick={handleSave} disabled={!formData.name}>{editingSetting ? "Update" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {settings.length === 0 ? (
          <Card className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Templates Created</h3>
            <p className="text-gray-500 mb-4">Create your first header/footer template to get started</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Template
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {settings.map((setting) => (
              <Card key={setting.id} className={`${setting.is_default ? "border-blue-500 border-2" : ""}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {setting.logo_url && (
                        <img src={setting.logo_url} alt="Logo" className="h-16 w-auto object-contain border rounded p-1" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-semibold">{setting.name}</h3>
                          {setting.is_default && (
                            <Badge className="bg-blue-100 text-blue-800">
                              <Star className="h-3 w-3 mr-1 fill-current" /> Default
                            </Badge>
                          )}
                        </div>
                        {setting.company_name && (
                          <p className="text-gray-700 font-medium">{setting.company_name}</p>
                        )}
                        {setting.tagline && (
                          <p className="text-gray-500 italic text-sm">{setting.tagline}</p>
                        )}
                        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-500">
                          {setting.company_email && <div><span className="font-medium">Email:</span> {setting.company_email}</div>}
                          {setting.company_phone && <div><span className="font-medium">Phone:</span> {setting.company_phone}</div>}
                          {setting.company_website && <div><span className="font-medium">Website:</span> {setting.company_website}</div>}
                          {setting.footer_text && <div><span className="font-medium">Footer:</span> {setting.footer_text}</div>}
                        </div>
                        {setting.logo_url && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            <FileText className="h-3 w-3 mr-1" /> Logo uploaded
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!setting.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleSetDefault(setting.id)}>
                          <Check className="h-4 w-4 mr-1" /> Set Default
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleEdit(setting)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(setting.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ==================== PAGE HEADER WITH SETTINGS SELECTOR ====================
const PageHeader = ({ title, description, onReset, onFillSample }) => {
  const navigate = useNavigate();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {settings.length > 0 && (
              <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                <SelectTrigger className="w-[180px]">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Header/Footer</SelectItem>
                  {settings.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.is_default && "★"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {onFillSample && (
              <Button variant="outline" size="sm" onClick={onFillSample} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample Data
              </Button>
            )}
            {onReset && (
              <Button variant="outline" size="sm" onClick={onReset} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// ==================== DYNAMIC LIST INPUT ====================
const DynamicListInput = ({ items, setItems, placeholder, label }) => {
  const addItem = () => setItems([...items, ""]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));
  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          <Input value={item} onChange={(e) => updateItem(index, e.target.value)} placeholder={placeholder} />
          <Button variant="ghost" size="icon" onClick={() => removeItem(index)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addItem}><Plus className="h-4 w-4 mr-2" /> Add Item</Button>
    </div>
  );
};

// ==================== BA REQUIREMENTS TEMPLATE ====================
const BARequirementsTemplate = () => {
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const savedTemplateIdRef = useRef(null);
  
  const getEmptyForm = () => ({
    project_name: "", client_name: "", date: new Date().toISOString().split("T")[0], prepared_by: "", version: "1.0",
    project_overview: "", business_objectives: [""], stakeholders: [{ name: "", role: "", responsibility: "" }],
    functional_requirements: [{ id: "FR-001", requirement: "", priority: "High", description: "" }],
    non_functional_requirements: [""], constraints: [""], assumptions: [""], out_of_scope: [""], acceptance_criteria: [""],
    timeline: "", sign_off_name: "", sign_off_role: ""
  });

  const getSampleData = () => ({
    project_name: "Customer Portal Development", client_name: "ABC Corporation", date: new Date().toISOString().split("T")[0],
    prepared_by: "John Smith", version: "1.0",
    project_overview: "Development of a comprehensive customer portal that enables clients to manage their accounts, view transaction history, submit support tickets, and access self-service features.",
    business_objectives: ["Reduce customer support calls by 40%", "Improve customer satisfaction to 90%", "Enable 24/7 account access", "Streamline billing processes"],
    stakeholders: [{ name: "Sarah Johnson", role: "Product Owner", responsibility: "Requirements approval" }, { name: "Mike Chen", role: "IT Director", responsibility: "Technical oversight" }],
    functional_requirements: [
      { id: "FR-001", requirement: "User Registration", priority: "High", description: "Email verification" },
      { id: "FR-002", requirement: "Dashboard", priority: "High", description: "Account overview" },
      { id: "FR-003", requirement: "Support Tickets", priority: "High", description: "Create and track requests" }
    ],
    non_functional_requirements: ["Handle 10,000 concurrent users", "Page load under 3 seconds", "99.9% uptime"],
    constraints: ["Budget: $150,000", "Deadline: Q2 2025"],
    assumptions: ["API documentation available", "Test data provided in 2 weeks"],
    out_of_scope: ["Mobile native apps", "Multi-language support (Phase 2)"],
    acceptance_criteria: ["All requirements tested", "Performance benchmarks met", "Security audit passed"],
    timeline: "16 weeks total", sign_off_name: "Sarah Johnson", sign_off_role: "Product Owner"
  });

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [formData, setFormData] = useState(getEmptyForm());
  const navigate = useNavigate();
  
  // Keep refs in sync with state
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  const loadSavedTemplate = async (id) => {
    try {
      const response = await axios.get(`${API}/saved-templates/${id}`);
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Auto-save ref to store latest formData
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      const interval = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
      autoSaveIntervalRef.current = interval;
      return () => clearInterval(interval);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
  }, [autoSave, user?.email]);

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      let version = dataToSave.version || '1.0';
      if (existingSavedId && !isAutoSave) {
        const vNum = parseFloat(version) || 1.0;
        version = (vNum + 0.1).toFixed(1);
      }
      
      const payload = {
        name: dataToSave.project_name || "BA Requirements Draft",
        template_type: "BA Requirements",
        template_id: "ba-requirements",
        format: "DOCX",
        data: { ...dataToSave, version }
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      
      if (version !== dataToSave.version) {
        setFormData(prev => ({ ...prev, version }));
      }
      
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (success && !isAutoSave) {
      alert("Draft saved successfully!");
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      // First save the draft
      await saveDraftInternal(formData, savedTemplateId, false);
      
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/ba-requirements?settings_id=${selectedSetting}`
        : `${API}/generate/ba-requirements`;
      const dataWithUser = { ...formData, created_by_email: user?.email || '' };
      const response = await axios.post(url, dataWithUser, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `BA_Requirements_${formData.project_name || "Document"}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.project_name || "BA Requirements Document",
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "BA Requirements",
        templateId: "ba-requirements",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "ba-requirements", "BA Requirements", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("ba-requirements", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const updateStakeholder = (i, field, value) => {
    const n = [...formData.stakeholders]; n[i][field] = value; setFormData({ ...formData, stakeholders: n });
  };
  const updateRequirement = (i, field, value) => {
    const n = [...formData.functional_requirements]; n[i][field] = value; setFormData({ ...formData, functional_requirements: n });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">BA Requirements Document</h1>
                <p className="text-sm text-gray-500">Business Requirements Document (BRD)</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]"><Building2 className="h-4 w-4 mr-2" /><SelectValue placeholder="Header/Footer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" /></div>
              <div><Label>Client Name *</Label><Input value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} placeholder="Client name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })} placeholder="Your name" /></div>
            </div>
            <Separator />
            <div><Label>Project Overview *</Label><Textarea value={formData.project_overview} onChange={(e) => setFormData({ ...formData, project_overview: e.target.value })} rows={4} placeholder="Describe the project..." /></div>
            <DynamicListInput items={formData.business_objectives} setItems={(items) => setFormData({ ...formData, business_objectives: items })} placeholder="Business objective" label="Business Objectives" />
            <Separator />
            <div className="space-y-2">
              <Label>Stakeholders</Label>
              {formData.stakeholders.map((s, i) => (
                <div key={i} className="grid grid-cols-3 gap-2">
                  <Input value={s.name} onChange={(e) => updateStakeholder(i, "name", e.target.value)} placeholder="Name" />
                  <Input value={s.role} onChange={(e) => updateStakeholder(i, "role", e.target.value)} placeholder="Role" />
                  <div className="flex gap-2">
                    <Input value={s.responsibility} onChange={(e) => updateStakeholder(i, "responsibility", e.target.value)} placeholder="Responsibility" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({ ...formData, stakeholders: formData.stakeholders.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, stakeholders: [...formData.stakeholders, { name: "", role: "", responsibility: "" }] })}><Plus className="h-4 w-4 mr-2" /> Add</Button>
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Functional Requirements</Label>
              {formData.functional_requirements.map((r, i) => (
                <div key={i} className="grid grid-cols-4 gap-2">
                  <Input value={r.id} onChange={(e) => updateRequirement(i, "id", e.target.value)} placeholder="FR-001" />
                  <Input value={r.requirement} onChange={(e) => updateRequirement(i, "requirement", e.target.value)} placeholder="Requirement" />
                  <Select value={r.priority} onValueChange={(v) => updateRequirement(i, "priority", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input value={r.description} onChange={(e) => updateRequirement(i, "description", e.target.value)} placeholder="Description" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({ ...formData, functional_requirements: formData.functional_requirements.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, functional_requirements: [...formData.functional_requirements, { id: `FR-${String(formData.functional_requirements.length + 1).padStart(3, "0")}`, requirement: "", priority: "High", description: "" }] })}><Plus className="h-4 w-4 mr-2" /> Add</Button>
            </div>
            <DynamicListInput items={formData.non_functional_requirements} setItems={(items) => setFormData({ ...formData, non_functional_requirements: items })} placeholder="Non-functional requirement" label="Non-Functional Requirements" />
            <DynamicListInput items={formData.constraints} setItems={(items) => setFormData({ ...formData, constraints: items })} placeholder="Constraint" label="Constraints" />
            <DynamicListInput items={formData.assumptions} setItems={(items) => setFormData({ ...formData, assumptions: items })} placeholder="Assumption" label="Assumptions" />
            <DynamicListInput items={formData.out_of_scope} setItems={(items) => setFormData({ ...formData, out_of_scope: items })} placeholder="Out of scope" label="Out of Scope" />
            <DynamicListInput items={formData.acceptance_criteria} setItems={(items) => setFormData({ ...formData, acceptance_criteria: items })} placeholder="Acceptance criteria" label="Acceptance Criteria" />
            <Separator />
            <div><Label>Timeline</Label><Textarea value={formData.timeline} onChange={(e) => setFormData({ ...formData, timeline: e.target.value })} rows={2} placeholder="Timeline..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Sign-off Name</Label><Input value={formData.sign_off_name} onChange={(e) => setFormData({ ...formData, sign_off_name: e.target.value })} placeholder="Client representative" /></div>
              <div><Label>Sign-off Role</Label><Input value={formData.sign_off_role} onChange={(e) => setFormData({ ...formData, sign_off_role: e.target.value })} placeholder="Role" /></div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== GENERIC TEMPLATE FORM ====================
const GenericTemplateForm = ({ templateId, title, description, format, endpoint, fields, getSampleData }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const savedTemplateIdRef = useRef(null);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // Load saved template data if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  const loadSavedTemplate = async (id) => {
    try {
      const response = await axios.get(`${API}/saved-templates/${id}`);
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  useEffect(() => {
    // Initialize form data with empty values based on fields
    const initialData = {};
    fields.forEach(field => {
      if (field.type === 'array') initialData[field.name] = field.default || [''];
      else if (field.type === 'object-array') initialData[field.name] = field.default || [{}];
      else if (field.type === 'object') initialData[field.name] = field.default || {};
      else initialData[field.name] = field.default || '';
    });
    if (!editingSavedId) {
      setFormData(initialData);
    }
  }, [fields, editingSavedId]);

  // Auto-save ref to store latest formData
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // Keep savedTemplateIdRef in sync
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!dataToSave || Object.keys(dataToSave).length === 0) {
      console.log('No form data to save');
      return false;
    }
    
    if (!isAutoSave) setSaving(true);
    
    try {
      const draftName = dataToSave.title || dataToSave.project_name || dataToSave.meeting_title || dataToSave.company_name || `${title} Draft`;
      
      // Auto-increment version if updating (not for auto-save)
      let version = dataToSave.version || '1.0';
      if (existingSavedId && !isAutoSave) {
        const vNum = parseFloat(version) || 1.0;
        version = (vNum + 0.1).toFixed(1);
      }
      
      const payload = {
        name: draftName,
        template_type: title,
        template_id: templateId,
        format: format,
        data: { ...dataToSave, version }
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};

      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      
      // Update version in form if changed
      if (version !== dataToSave.version) {
        setFormData(prev => ({ ...prev, version }));
      }
      
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) {
        setSaving(false);
        alert("Error saving draft: " + (error.response?.data?.error || error.message));
      }
      return false;
    }
  };

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    let interval;
    if (autoSave && user?.email) {
      interval = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoSave, user?.email]);

  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      // First save the draft
      await saveDraftInternal(formData, savedTemplateId, false);
      
      let url = `${API}/generate/${endpoint}`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const dataWithUser = { ...formData, created_by_email: user?.email || '' };
      const response = await axios.post(url, dataWithUser, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const ext = format === "DOCX" ? "docx" : "xlsx";
      link.setAttribute("download", `${templateId.replace(/-/g, '_')}_${Date.now()}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || title,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: title,
        templateId: templateId,
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, templateId, title, user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate(templateId, result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const updateField = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const updateArrayItem = (name, index, value) => {
    const arr = [...(formData[name] || [])];
    arr[index] = value;
    setFormData({ ...formData, [name]: arr });
  };

  const addArrayItem = (name, defaultValue = '') => {
    setFormData({ ...formData, [name]: [...(formData[name] || []), defaultValue] });
  };

  const removeArrayItem = (name, index) => {
    setFormData({ ...formData, [name]: (formData[name] || []).filter((_, i) => i !== index) });
  };

  const updateObjectArrayItem = (name, index, field, value) => {
    const arr = [...(formData[name] || [])];
    arr[index] = { ...arr[index], [field]: value };
    setFormData({ ...formData, [name]: arr });
  };

  const addObjectArrayItem = (name, template = {}) => {
    setFormData({ ...formData, [name]: [...(formData[name] || []), template] });
  };

  const removeObjectArrayItem = (name, index) => {
    setFormData({ ...formData, [name]: (formData[name] || []).filter((_, i) => i !== index) });
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <div key={field.name}>
            <Label>{field.label}{field.required && ' *'}</Label>
            <Input value={formData[field.name] || ''} onChange={(e) => updateField(field.name, e.target.value)} placeholder={field.placeholder} />
          </div>
        );
      case 'textarea':
        return (
          <div key={field.name}>
            <Label>{field.label}{field.required && ' *'}</Label>
            <Textarea value={formData[field.name] || ''} onChange={(e) => updateField(field.name, e.target.value)} placeholder={field.placeholder} rows={field.rows || 3} />
          </div>
        );
      case 'date':
        return (
          <div key={field.name}>
            <Label>{field.label}</Label>
            <Input type="date" value={formData[field.name] || ''} onChange={(e) => updateField(field.name, e.target.value)} />
          </div>
        );
      case 'number':
        return (
          <div key={field.name}>
            <Label>{field.label}</Label>
            <Input type="number" value={formData[field.name] || ''} onChange={(e) => updateField(field.name, parseInt(e.target.value) || 0)} placeholder={field.placeholder} />
          </div>
        );
      case 'select':
        return (
          <div key={field.name}>
            <Label>{field.label}</Label>
            <Select value={formData[field.name] || ''} onValueChange={(v) => updateField(field.name, v)}>
              <SelectTrigger><SelectValue placeholder={field.placeholder} /></SelectTrigger>
              <SelectContent>
                {field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        );
      case 'array':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            {(formData[field.name] || []).map((item, i) => (
              <div key={i} className="flex gap-2">
                <Input value={item} onChange={(e) => updateArrayItem(field.name, i, e.target.value)} placeholder={field.itemPlaceholder} />
                <Button variant="ghost" size="icon" onClick={() => removeArrayItem(field.name, i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addArrayItem(field.name)}><Plus className="h-4 w-4 mr-2" /> Add</Button>
          </div>
        );
      case 'object-array':
        return (
          <div key={field.name} className="space-y-2">
            <Label>{field.label}</Label>
            {(formData[field.name] || []).map((item, i) => (
              <div key={i} className={`grid grid-cols-${field.fields.length + 1} gap-2`} style={{gridTemplateColumns: `repeat(${field.fields.length}, 1fr) auto`}}>
                {field.fields.map(f => (
                  <Input key={f.name} value={item[f.name] || ''} onChange={(e) => updateObjectArrayItem(field.name, i, f.name, e.target.value)} placeholder={f.placeholder} />
                ))}
                <Button variant="ghost" size="icon" onClick={() => removeObjectArrayItem(field.name, i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addObjectArrayItem(field.name, field.template || {})}><Plus className="h-4 w-4 mr-2" /> Add</Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{title}</h1>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]"><Building2 className="h-4 w-4 mr-2" /><SelectValue placeholder="Header/Footer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              {getSampleData && (
                <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => {
                const initialData = {};
                fields.forEach(field => {
                  if (field.type === 'array') initialData[field.name] = field.default || [''];
                  else if (field.type === 'object-array') initialData[field.name] = field.default || [{}];
                  else if (field.type === 'object') initialData[field.name] = field.default || {};
                  else initialData[field.name] = field.default || '';
                });
                setFormData(initialData);
                setSavedTemplateId(null);
              }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.filter(f => f.section === 'header').map(renderField)}
            </div>
            {fields.filter(f => !f.section || f.section === 'main').length > 0 && <Separator />}
            {fields.filter(f => !f.section || f.section === 'main').map(renderField)}
            <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || saving} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Saving & Generating..." : `Download ${format}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== MINUTES OF MEETING TEMPLATE ====================
const MinutesOfMeetingTemplate = () => {
  const fields = [
    { name: 'meeting_title', label: 'Meeting Title', type: 'text', placeholder: 'Sprint Review Meeting', required: true, section: 'header' },
    { name: 'date', label: 'Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'time', label: 'Time', type: 'text', placeholder: '10:00 AM', section: 'header' },
    { name: 'location', label: 'Location', type: 'text', placeholder: 'Conference Room A / Zoom', section: 'header' },
    { name: 'prepared_by', label: 'Prepared By', type: 'text', placeholder: 'Your name', section: 'header' },
    { name: 'attendees', label: 'Attendees', type: 'object-array', fields: [{name: 'name', placeholder: 'Name'}, {name: 'role', placeholder: 'Role'}, {name: 'department', placeholder: 'Department'}], template: {name: '', role: '', department: ''} },
    { name: 'absentees', label: 'Absentees', type: 'array', itemPlaceholder: 'Name of absentee' },
    { name: 'agenda_items', label: 'Agenda Items', type: 'array', itemPlaceholder: 'Agenda item' },
    { name: 'discussion_points', label: 'Discussion Points', type: 'object-array', fields: [{name: 'topic', placeholder: 'Topic'}, {name: 'discussion', placeholder: 'Discussion'}, {name: 'outcome', placeholder: 'Outcome'}], template: {topic: '', discussion: '', outcome: ''} },
    { name: 'decisions_made', label: 'Decisions Made', type: 'array', itemPlaceholder: 'Decision' },
    { name: 'action_items', label: 'Action Items', type: 'object-array', fields: [{name: 'action', placeholder: 'Action'}, {name: 'assigned_to', placeholder: 'Assigned To'}, {name: 'due_date', placeholder: 'Due Date'}, {name: 'status', placeholder: 'Status'}], template: {action: '', assigned_to: '', due_date: '', status: 'Pending'} },
    { name: 'next_meeting_date', label: 'Next Meeting Date', type: 'text', placeholder: '2025-01-20' },
    { name: 'next_meeting_time', label: 'Next Meeting Time', type: 'text', placeholder: '10:00 AM' },
    { name: 'approved_by', label: 'Approved By', type: 'text', placeholder: 'Approver name' }
  ];

  const getSampleData = () => ({
    meeting_title: 'Sprint 5 Review Meeting',
    date: new Date().toISOString().split('T')[0],
    time: '10:00 AM',
    location: 'Conference Room A',
    prepared_by: 'John Smith',
    attendees: [{name: 'John Smith', role: 'Scrum Master', department: 'Engineering'}, {name: 'Jane Doe', role: 'Product Owner', department: 'Product'}],
    absentees: ['Mike Johnson (On leave)'],
    agenda_items: ['Review sprint goals', 'Demo completed features', 'Discuss blockers', 'Plan next sprint'],
    discussion_points: [{topic: 'Sprint Progress', discussion: 'Team completed 85% of planned stories', outcome: 'On track for release'}, {topic: 'Technical Debt', discussion: 'Need to address performance issues', outcome: 'Allocated 2 days next sprint'}],
    decisions_made: ['Deploy to staging on Friday', 'Add 2 more story points to next sprint'],
    action_items: [{action: 'Prepare staging environment', assigned_to: 'DevOps Team', due_date: '2025-01-17', status: 'Pending'}, {action: 'Update documentation', assigned_to: 'Jane Doe', due_date: '2025-01-18', status: 'Pending'}],
    next_meeting_date: '2025-01-20',
    next_meeting_time: '10:00 AM',
    approved_by: 'Jane Doe'
  });

  return <GenericTemplateForm templateId="minutes-of-meeting" title="Minutes of Meeting (MoM)" description="Document meeting discussions, decisions, and action items" format="DOCX" endpoint="minutes-of-meeting" fields={fields} getSampleData={getSampleData} />;
};

// ==================== MEETING AGENDA TEMPLATE ====================
const MeetingAgendaTemplate = () => {
  const fields = [
    { name: 'meeting_title', label: 'Meeting Title', type: 'text', placeholder: 'Weekly Team Sync', required: true, section: 'header' },
    { name: 'date', label: 'Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'time', label: 'Time', type: 'text', placeholder: '10:00 AM', section: 'header' },
    { name: 'location', label: 'Location', type: 'text', placeholder: 'Conference Room B', section: 'header' },
    { name: 'organizer', label: 'Organizer', type: 'text', placeholder: 'Your name', section: 'header' },
    { name: 'attendees', label: 'Attendees', type: 'array', itemPlaceholder: 'Attendee name' },
    { name: 'objectives', label: 'Meeting Objectives', type: 'array', itemPlaceholder: 'Objective' },
    { name: 'agenda_items', label: 'Agenda Items', type: 'object-array', fields: [{name: 'time', placeholder: 'Time (e.g., 10:00)'}, {name: 'topic', placeholder: 'Topic'}, {name: 'presenter', placeholder: 'Presenter'}, {name: 'duration', placeholder: 'Duration'}], template: {time: '', topic: '', presenter: '', duration: ''} },
    { name: 'documents_required', label: 'Pre-read Documents', type: 'array', itemPlaceholder: 'Document name' },
    { name: 'notes', label: 'Additional Notes', type: 'textarea', placeholder: 'Any additional notes...' }
  ];

  const getSampleData = () => ({
    meeting_title: 'Sprint Planning Meeting',
    date: new Date().toISOString().split('T')[0],
    time: '09:00 AM',
    location: 'Main Conference Room',
    organizer: 'John Smith',
    attendees: ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'],
    objectives: ['Define sprint goals', 'Estimate user stories', 'Assign tasks to team members'],
    agenda_items: [
      {time: '09:00', topic: 'Review previous sprint', presenter: 'John Smith', duration: '15 min'},
      {time: '09:15', topic: 'Backlog grooming', presenter: 'Jane Doe', duration: '30 min'},
      {time: '09:45', topic: 'Story estimation', presenter: 'Team', duration: '45 min'},
      {time: '10:30', topic: 'Task assignment', presenter: 'John Smith', duration: '20 min'}
    ],
    documents_required: ['Product Backlog', 'Sprint Velocity Report', 'Technical Debt List'],
    notes: 'Please review the backlog items before the meeting'
  });

  return <GenericTemplateForm templateId="meeting-agenda" title="Meeting Agenda" description="Plan and organize meeting topics and objectives" format="DOCX" endpoint="meeting-agenda" fields={fields} getSampleData={getSampleData} />;
};

// ==================== ACTION ITEM TRACKER TEMPLATE ====================
const ActionItemTrackerTemplate = () => {
  const fields = [
    { name: 'project_name', label: 'Project Name', type: 'text', placeholder: 'Project name', required: true, section: 'header' },
    { name: 'date', label: 'Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'action_items', label: 'Action Items', type: 'object-array', fields: [
      {name: 'id', placeholder: 'ID (AI-001)'},
      {name: 'action', placeholder: 'Action Item'},
      {name: 'description', placeholder: 'Description'},
      {name: 'assigned_to', placeholder: 'Assigned To'},
      {name: 'priority', placeholder: 'Priority'},
      {name: 'due_date', placeholder: 'Due Date'},
      {name: 'status', placeholder: 'Status'},
      {name: 'completion', placeholder: '% Complete'},
      {name: 'notes', placeholder: 'Notes'}
    ], template: {id: '', action: '', description: '', assigned_to: '', priority: 'Medium', due_date: '', status: 'Not Started', completion: 0, notes: ''} }
  ];

  const getSampleData = () => ({
    project_name: 'CRM Implementation',
    date: new Date().toISOString().split('T')[0],
    action_items: [
      {id: 'AI-001', action: 'Finalize database schema', description: 'Complete ER diagram and get approval', assigned_to: 'Mike Johnson', priority: 'High', due_date: '2025-01-20', status: 'In Progress', completion: 60, notes: 'Pending DBA review'},
      {id: 'AI-002', action: 'Setup CI/CD pipeline', description: 'Configure Jenkins for automated builds', assigned_to: 'Sarah Wilson', priority: 'Medium', due_date: '2025-01-25', status: 'Not Started', completion: 0, notes: ''},
      {id: 'AI-003', action: 'Create user documentation', description: 'Write end-user guide for main features', assigned_to: 'Jane Doe', priority: 'Low', due_date: '2025-01-30', status: 'Not Started', completion: 0, notes: 'After UAT completion'}
    ]
  });

  return <GenericTemplateForm templateId="action-item-tracker" title="Action Item Tracker" description="Track and manage action items with status and deadlines" format="XLSX" endpoint="action-item-tracker" fields={fields} getSampleData={getSampleData} />;
};

// ==================== SPRINT PLANNING TEMPLATE ====================
const SprintPlanningTemplate = () => {
  const fields = [
    { name: 'sprint_name', label: 'Sprint Name', type: 'text', placeholder: 'Sprint 5', required: true, section: 'header' },
    { name: 'sprint_number', label: 'Sprint Number', type: 'number', placeholder: '5', section: 'header' },
    { name: 'start_date', label: 'Start Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'end_date', label: 'End Date', type: 'date', section: 'header' },
    { name: 'team_name', label: 'Team Name', type: 'text', placeholder: 'Alpha Team', section: 'header' },
    { name: 'sprint_goal', label: 'Sprint Goal', type: 'textarea', placeholder: 'Complete user authentication module...', rows: 2 },
    { name: 'team_capacity', label: 'Team Capacity', type: 'object-array', fields: [
      {name: 'name', placeholder: 'Name'},
      {name: 'role', placeholder: 'Role'},
      {name: 'available_days', placeholder: 'Available Days'},
      {name: 'hours_per_day', placeholder: 'Hours/Day'}
    ], template: {name: '', role: '', available_days: 10, hours_per_day: 6} },
    { name: 'user_stories', label: 'User Stories', type: 'object-array', fields: [
      {name: 'story_id', placeholder: 'Story ID'},
      {name: 'user_story', placeholder: 'User Story'},
      {name: 'priority', placeholder: 'Priority'},
      {name: 'story_points', placeholder: 'Story Points'},
      {name: 'assigned_to', placeholder: 'Assigned To'},
      {name: 'status', placeholder: 'Status'}
    ], template: {story_id: '', user_story: '', priority: 'High', story_points: 0, assigned_to: '', status: 'To Do'} },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Additional notes...' }
  ];

  const getSampleData = () => ({
    sprint_name: 'Sprint 5',
    sprint_number: 5,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 14*24*60*60*1000).toISOString().split('T')[0],
    team_name: 'Alpha Team',
    sprint_goal: 'Complete user authentication module with OAuth2 integration and password reset functionality',
    team_capacity: [
      {name: 'John Smith', role: 'Senior Developer', available_days: 10, hours_per_day: 6},
      {name: 'Jane Doe', role: 'Frontend Developer', available_days: 8, hours_per_day: 6},
      {name: 'Mike Johnson', role: 'QA Engineer', available_days: 10, hours_per_day: 6}
    ],
    user_stories: [
      {story_id: 'US-101', user_story: 'As a user, I want to login with email and password', priority: 'High', story_points: 5, assigned_to: 'John Smith', status: 'To Do'},
      {story_id: 'US-102', user_story: 'As a user, I want to reset my password via email', priority: 'High', story_points: 8, assigned_to: 'John Smith', status: 'To Do'},
      {story_id: 'US-103', user_story: 'As a user, I want to login with Google OAuth', priority: 'Medium', story_points: 8, assigned_to: 'Jane Doe', status: 'To Do'}
    ],
    notes: 'Dependencies: OAuth credentials from DevOps team'
  });

  return <GenericTemplateForm templateId="sprint-planning" title="Sprint Planning Sheet" description="Plan sprint capacity, user stories, and team allocation" format="XLSX" endpoint="sprint-planning" fields={fields} getSampleData={getSampleData} />;
};

// ==================== DAILY STANDUP TEMPLATE ====================
const DailyStandupTemplate = () => {
  const fields = [
    { name: 'sprint_name', label: 'Sprint Name', type: 'text', placeholder: 'Sprint 5', required: true, section: 'header' },
    { name: 'team_name', label: 'Team Name', type: 'text', placeholder: 'Alpha Team', section: 'header' },
    { name: 'start_date', label: 'Start Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'end_date', label: 'End Date', type: 'date', section: 'header' },
    { name: 'team_members', label: 'Team Members', type: 'array', itemPlaceholder: 'Team member name' },
    { name: 'standup_entries', label: 'Standup Entries', type: 'object-array', fields: [
      {name: 'date', placeholder: 'Date'},
      {name: 'team_member', placeholder: 'Team Member'},
      {name: 'yesterday', placeholder: "Yesterday's Work"},
      {name: 'today', placeholder: "Today's Plan"},
      {name: 'blockers', placeholder: 'Blockers'},
      {name: 'status', placeholder: 'Status'},
      {name: 'story_id', placeholder: 'Story ID'}
    ], template: {date: '', team_member: '', yesterday: '', today: '', blockers: '', status: 'On Track', story_id: ''} }
  ];

  const getSampleData = () => ({
    sprint_name: 'Sprint 5',
    team_name: 'Alpha Team',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 14*24*60*60*1000).toISOString().split('T')[0],
    team_members: ['John Smith', 'Jane Doe', 'Mike Johnson'],
    standup_entries: [
      {date: new Date().toISOString().split('T')[0], team_member: 'John Smith', yesterday: 'Completed login API', today: 'Start password reset feature', blockers: 'None', status: 'On Track', story_id: 'US-101'},
      {date: new Date().toISOString().split('T')[0], team_member: 'Jane Doe', yesterday: 'Finished login UI', today: 'OAuth integration UI', blockers: 'Waiting for API docs', status: 'At Risk', story_id: 'US-103'}
    ]
  });

  return <GenericTemplateForm templateId="daily-standup" title="Daily Standup Tracker" description="Track daily standup updates and blockers" format="XLSX" endpoint="daily-standup" fields={fields} getSampleData={getSampleData} />;
};

// ==================== SPRINT RETROSPECTIVE TEMPLATE ====================
const SprintRetrospectiveTemplate = () => {
  const fields = [
    { name: 'sprint_name', label: 'Sprint Name', type: 'text', placeholder: 'Sprint 5', required: true, section: 'header' },
    { name: 'sprint_number', label: 'Sprint Number', type: 'number', placeholder: '5', section: 'header' },
    { name: 'date', label: 'Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'team_name', label: 'Team Name', type: 'text', placeholder: 'Alpha Team', section: 'header' },
    { name: 'facilitator', label: 'Facilitator', type: 'text', placeholder: 'Scrum Master name', section: 'header' },
    { name: 'sprint_rating', label: 'Sprint Rating (1-10)', type: 'number', placeholder: '8', section: 'header' },
    { name: 'attendees', label: 'Attendees', type: 'array', itemPlaceholder: 'Attendee name' },
    { name: 'what_went_well', label: 'What Went Well', type: 'array', itemPlaceholder: 'Positive item' },
    { name: 'what_didnt_go_well', label: "What Didn't Go Well", type: 'array', itemPlaceholder: 'Area for improvement' },
    { name: 'action_items', label: 'Action Items', type: 'object-array', fields: [
      {name: 'action', placeholder: 'Action'},
      {name: 'owner', placeholder: 'Owner'},
      {name: 'due_date', placeholder: 'Due Date'}
    ], template: {action: '', owner: '', due_date: ''} },
    { name: 'additional_comments', label: 'Additional Comments', type: 'textarea', placeholder: 'Any other comments...' }
  ];

  const getSampleData = () => ({
    sprint_name: 'Sprint 5',
    sprint_number: 5,
    date: new Date().toISOString().split('T')[0],
    team_name: 'Alpha Team',
    facilitator: 'John Smith',
    sprint_rating: 8,
    attendees: ['John Smith', 'Jane Doe', 'Mike Johnson', 'Sarah Wilson'],
    what_went_well: ['Completed all planned stories', 'Great team collaboration', 'Zero critical bugs in production', 'Effective daily standups'],
    what_didnt_go_well: ['Late requirement changes', 'Deployment process was slow', 'Documentation was delayed'],
    action_items: [
      {action: 'Improve deployment automation', owner: 'DevOps Team', due_date: '2025-01-25'},
      {action: 'Create documentation template', owner: 'Jane Doe', due_date: '2025-01-20'}
    ],
    additional_comments: 'Overall a successful sprint. Team morale is high.'
  });

  return <GenericTemplateForm templateId="sprint-retrospective" title="Sprint Retrospective" description="Document what went well and areas for improvement" format="DOCX" endpoint="sprint-retrospective" fields={fields} getSampleData={getSampleData} />;
};

// ==================== CHANGE REQUEST TEMPLATE ====================
const ChangeRequestTemplate = () => {
  const fields = [
    { name: 'change_request_id', label: 'Change Request ID', type: 'text', placeholder: 'CR-001', required: true, section: 'header' },
    { name: 'project_name', label: 'Project Name', type: 'text', placeholder: 'Project name', required: true, section: 'header' },
    { name: 'date', label: 'Date', type: 'date', default: new Date().toISOString().split('T')[0], section: 'header' },
    { name: 'requested_by', label: 'Requested By', type: 'text', placeholder: 'Requestor name', section: 'header' },
    { name: 'priority', label: 'Priority', type: 'select', options: ['Critical', 'High', 'Medium', 'Low'], section: 'header' },
    { name: 'status', label: 'Status', type: 'select', options: ['Pending', 'Approved', 'Rejected', 'On Hold'], section: 'header' },
    { name: 'change_title', label: 'Change Title', type: 'text', placeholder: 'Brief title of the change' },
    { name: 'change_description', label: 'Change Description', type: 'textarea', placeholder: 'Detailed description of the change...', rows: 3 },
    { name: 'reason_for_change', label: 'Reason for Change', type: 'textarea', placeholder: 'Why is this change needed?', rows: 2 },
    { name: 'proposed_solution', label: 'Proposed Solution', type: 'textarea', placeholder: 'How will this change be implemented?', rows: 2 },
    { name: 'cost_impact', label: 'Cost Impact', type: 'text', placeholder: 'e.g., $10,000 additional budget' },
    { name: 'timeline_impact', label: 'Timeline Impact', type: 'text', placeholder: 'e.g., 2 weeks delay' },
    { name: 'risk_assessment', label: 'Risk Assessment', type: 'textarea', placeholder: 'Potential risks and mitigation...', rows: 2 },
    { name: 'approvers', label: 'Approvers', type: 'object-array', fields: [
      {name: 'name', placeholder: 'Name'},
      {name: 'role', placeholder: 'Role'},
      {name: 'decision', placeholder: 'Decision'},
      {name: 'date', placeholder: 'Date'},
      {name: 'comments', placeholder: 'Comments'}
    ], template: {name: '', role: '', decision: 'Pending', date: '', comments: ''} }
  ];

  const getSampleData = () => ({
    change_request_id: 'CR-001',
    project_name: 'CRM Implementation',
    date: new Date().toISOString().split('T')[0],
    requested_by: 'John Smith',
    priority: 'High',
    status: 'Pending',
    change_title: 'Add Email Integration with SendGrid',
    change_description: 'Integrate the CRM system with SendGrid for automated email notifications. This includes lead notification emails, follow-up reminders, and campaign emails.',
    reason_for_change: 'Customer requested automated email notifications for sales leads to improve response time and conversion rates.',
    proposed_solution: 'Use SendGrid API for transactional emails. Implement email templates for different notification types. Add email tracking for analytics.',
    cost_impact: '$5,000 additional budget for SendGrid subscription and development effort',
    timeline_impact: '2 weeks additional development time',
    risk_assessment: 'Low risk - SendGrid is a well-documented, reliable service. Mitigation: Implement retry logic and fallback email provider.',
    impact_analysis: {Scope: 'Medium - New feature addition', Timeline: '2 weeks delay', Cost: '$5,000', Resources: 'No additional resources needed'},
    approvers: [
      {name: 'Jane Doe', role: 'Project Manager', decision: 'Pending', date: '', comments: ''},
      {name: 'Mike Johnson', role: 'Technical Lead', decision: 'Pending', date: '', comments: ''}
    ]
  });

  return <GenericTemplateForm templateId="change-request" title="Change Request Form" description="Document and track project change requests" format="DOCX" endpoint="change-request" fields={fields} getSampleData={getSampleData} />;
};

// ==================== GO-LIVE CHECKLIST TEMPLATE ====================
const GoLiveChecklistTemplate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  const getEmptyForm = () => ({
    project_name: '',
    go_live_date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    checklist_sections: [{section_name: '', items: [{item: '', status: '', owner: ''}]}],
    sign_off_required: [{name: '', role: ''}]
  });

  const getSampleData = () => ({
    project_name: 'CRM System',
    go_live_date: new Date().toISOString().split('T')[0],
    prepared_by: 'John Smith',
    checklist_sections: [
      {section_name: 'Pre-Deployment', items: [{item: 'Code freeze completed', status: 'Complete', owner: 'Dev Team'}, {item: 'All tests passing', status: 'Complete', owner: 'QA'}, {item: 'Database backup taken', status: 'Pending', owner: 'DBA'}]},
      {section_name: 'Infrastructure', items: [{item: 'Production servers ready', status: 'Complete', owner: 'DevOps'}, {item: 'SSL certificates installed', status: 'Complete', owner: 'DevOps'}]},
      {section_name: 'Post-Deployment', items: [{item: 'Smoke tests executed', status: 'Pending', owner: 'QA'}, {item: 'Monitoring alerts configured', status: 'Pending', owner: 'DevOps'}]}
    ],
    sign_off_required: [
      {name: 'Jane Doe', role: 'Project Manager'},
      {name: 'Mike Johnson', role: 'Technical Lead'}
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());

  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      const loadSavedTemplate = async () => {
        try {
          const headers = user?.email ? { 'X-User-Email': user.email } : {};
          const response = await axios.get(`${API}/saved-templates/${editingSavedId}`, { headers });
          if (response.data && response.data.data) {
            setFormData(response.data.data);
            setSavedTemplateId(editingSavedId);
          }
        } catch (error) {
          console.error("Error loading saved template:", error);
        }
      };
      loadSavedTemplate();
    }
  }, [editingSavedId, user?.email]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.project_name || "Go-Live Checklist Draft",
        template_type: "Go-Live Checklist",
        template_id: "go-live-checklist",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "go-live-checklist", "Go-Live Checklist", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("go-live-checklist", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const handleDownloadPDF = async () => {
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `Go-Live Checklist - ${formData.project_name || 'Document'}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "Go-Live Checklist",
        templateId: "go-live-checklist",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
    setPdfLoading(false);
  };

  const handleDownload = async () => {
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/go-live-checklist`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Go_Live_Checklist_${formData.project_name || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const updateSection = (sectionIdx, field, value) => {
    const sections = [...formData.checklist_sections];
    sections[sectionIdx][field] = value;
    setFormData({...formData, checklist_sections: sections});
  };

  const updateSectionItem = (sectionIdx, itemIdx, field, value) => {
    const sections = [...formData.checklist_sections];
    sections[sectionIdx].items[itemIdx][field] = value;
    setFormData({...formData, checklist_sections: sections});
  };

  const addSectionItem = (sectionIdx) => {
    const sections = [...formData.checklist_sections];
    sections[sectionIdx].items.push({item: '', status: '', owner: ''});
    setFormData({...formData, checklist_sections: sections});
  };

  const removeSectionItem = (sectionIdx, itemIdx) => {
    const sections = [...formData.checklist_sections];
    sections[sectionIdx].items = sections[sectionIdx].items.filter((_, i) => i !== itemIdx);
    setFormData({...formData, checklist_sections: sections});
  };

  const addSection = () => {
    setFormData({...formData, checklist_sections: [...formData.checklist_sections, {section_name: '', items: [{item: '', status: '', owner: ''}]}]});
  };

  const removeSection = (sectionIdx) => {
    setFormData({...formData, checklist_sections: formData.checklist_sections.filter((_, i) => i !== sectionIdx)});
  };

  const updateSignOff = (idx, field, value) => {
    const signOffs = [...formData.sign_off_required];
    signOffs[idx][field] = value;
    setFormData({...formData, sign_off_required: signOffs});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Go-Live Checklist</h1>
                <p className="text-sm text-gray-500">Pre-deployment checklist for go-live readiness</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]"><Building2 className="h-4 w-4 mr-2" /><SelectValue placeholder="Header/Footer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" />
              </div>
              <div>
                <Label>Go-Live Date</Label>
                <Input type="date" value={formData.go_live_date} onChange={(e) => setFormData({...formData, go_live_date: e.target.value})} />
              </div>
              <div>
                <Label>Prepared By</Label>
                <Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Your name" />
              </div>
            </div>

            <Separator />
            
            <div className="space-y-4">
              <Label className="text-base font-semibold">Checklist Sections</Label>
              {formData.checklist_sections.map((section, sIdx) => (
                <Card key={sIdx} className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <Input className="max-w-xs font-semibold" value={section.section_name} onChange={(e) => updateSection(sIdx, 'section_name', e.target.value)} placeholder="Section Name (e.g., Pre-Deployment)" />
                    <Button variant="ghost" size="sm" onClick={() => removeSection(sIdx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                  <div className="space-y-2">
                    {section.items.map((item, iIdx) => (
                      <div key={iIdx} className="grid grid-cols-4 gap-2">
                        <Input className="col-span-2" value={item.item} onChange={(e) => updateSectionItem(sIdx, iIdx, 'item', e.target.value)} placeholder="Checklist item" />
                        <Select value={item.status} onValueChange={(v) => updateSectionItem(sIdx, iIdx, 'status', v)}>
                          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Complete">Complete</SelectItem>
                            <SelectItem value="N/A">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-1">
                          <Input value={item.owner} onChange={(e) => updateSectionItem(sIdx, iIdx, 'owner', e.target.value)} placeholder="Owner" />
                          <Button variant="ghost" size="icon" onClick={() => removeSectionItem(sIdx, iIdx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addSectionItem(sIdx)}><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addSection}><Plus className="h-4 w-4 mr-2" /> Add Section</Button>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Sign-off Required</Label>
              {formData.sign_off_required.map((so, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2">
                  <Input value={so.name} onChange={(e) => updateSignOff(idx, 'name', e.target.value)} placeholder="Name" />
                  <Input value={so.role} onChange={(e) => updateSignOff(idx, 'role', e.target.value)} placeholder="Role" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, sign_off_required: formData.sign_off_required.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, sign_off_required: [...formData.sign_off_required, {name: '', role: ''}]})}><Plus className="h-4 w-4 mr-2" /> Add</Button>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== PLACEHOLDER TEMPLATES ====================
const PlaceholderTemplate = ({ title, format, endpoint }) => {
  const navigate = useNavigate();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // Redirect to specific template forms based on endpoint
  const templateForms = {
    'okr': OKRTemplateForm,
    'kpi-dashboard': KPIDashboardForm,
    'raci-matrix': RACIMatrixForm,
    'strategic-roadmap': StrategicRoadmapForm,
    'budget-planning': BudgetPlanningForm,
    'financial-forecast': FinancialForecastForm,
    'roi-analysis': ROIAnalysisForm,
    'vendor-comparison': VendorComparisonForm,
    'capacity-planning': CapacityPlanningForm,
    'resource-allocation': ResourceAllocationForm,
    'hiring-plan': HiringPlanForm,
    'skills-matrix': SkillsMatrixForm,
    'technical-design': TechnicalDesignForm,
    'release-notes': ReleaseNotesForm,
    'incident-report': IncidentReportForm,
    'post-mortem': PostMortemForm,
    'runbook': RunbookForm,
    'api-documentation': APIDocumentationForm,
    'executive-summary': ExecutiveSummaryForm,
    'board-report': BoardReportForm,
    'qbr': QBRForm,
    'investor-update': InvestorUpdateForm
  };

  if (endpoint && templateForms[endpoint]) {
    const TemplateForm = templateForms[endpoint];
    return <TemplateForm />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
            <h1 className="text-xl font-bold">{title}</h1>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="p-8 text-center">
          <p className="text-gray-500">Form coming soon for {title}</p>
          <Button className="mt-4" onClick={() => navigate("/")}>Back to Dashboard</Button>
        </Card>
      </main>
    </div>
  );
};

// ==================== OKR TEMPLATE FORM ====================
const OKRTemplateForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    company_name: '', department: '', period: 'Q1', year: new Date().getFullYear().toString(),
    prepared_by: '', date: new Date().toISOString().split('T')[0],
    objectives: [{obj_id: 'O1', objective: '', owner: '', status: 'Not Started', progress: 0, key_results: [{kr_id: 'KR1.1', description: '', metric: '', target_value: '', current_value: '', status: 'Not Started', due_date: ''}]}],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'Acme Corp', department: 'Engineering', period: 'Q1', year: '2025',
    prepared_by: 'John Smith', date: new Date().toISOString().split('T')[0],
    objectives: [
      {obj_id: 'O1', objective: 'Improve Platform Reliability', owner: 'CTO', status: 'In Progress', progress: 45,
        key_results: [
          {kr_id: 'KR1.1', description: 'Achieve 99.9% uptime', metric: 'Uptime %', target_value: '99.9', current_value: '99.5', status: 'In Progress', due_date: '2025-03-31'},
          {kr_id: 'KR1.2', description: 'Reduce P1 incidents by 50%', metric: 'Incident count', target_value: '5', current_value: '8', status: 'At Risk', due_date: '2025-03-31'}
        ]},
      {obj_id: 'O2', objective: 'Accelerate Feature Delivery', owner: 'VP Engineering', status: 'Not Started', progress: 0,
        key_results: [
          {kr_id: 'KR2.1', description: 'Reduce deployment time to 30 min', metric: 'Minutes', target_value: '30', current_value: '120', status: 'Not Started', due_date: '2025-03-31'}
        ]}
    ],
    rai_assignments: [{task: 'Monitor SLA metrics', responsible: 'DevOps Lead', accountable: 'CTO', informed: 'Engineering Team', due_date: '2025-01-31', status: 'In Progress'}],
    followups: [{item: 'Review OKR progress', owner: 'CTO', due_date: '2025-02-15', priority: 'High', status: 'Pending'}]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);
  
  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };
  
  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);
  
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.company_name ? `OKR - ${dataToSave.company_name} ${dataToSave.period}` : "OKR Draft",
        template_type: "OKR Template",
        template_id: "okr",
        format: "XLSX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/okr?settings_id=${selectedSetting}`
        : `${API}/generate/okr`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `OKR_${formData.company_name || 'Document'}_${formData.period}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const addObjective = () => {
    const newId = `O${formData.objectives.length + 1}`;
    setFormData({...formData, objectives: [...formData.objectives, {obj_id: newId, objective: '', owner: '', status: 'Not Started', progress: 0, key_results: [{kr_id: `KR${formData.objectives.length + 1}.1`, description: '', metric: '', target_value: '', current_value: '', status: 'Not Started', due_date: ''}]}]});
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `OKR - ${formData.company_name || 'Document'} ${formData.period}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "OKR Template",
        templateId: "okr",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "okr", "OKR Template", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("okr", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const addKeyResult = (objIdx) => {
    const objs = [...formData.objectives];
    const krNum = objs[objIdx].key_results.length + 1;
    objs[objIdx].key_results.push({kr_id: `KR${objIdx+1}.${krNum}`, description: '', metric: '', target_value: '', current_value: '', status: 'Not Started', due_date: ''});
    setFormData({...formData, objectives: objs});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold">OKR Template</h1>
                <p className="text-sm text-gray-500">Objectives & Key Results with RAI assignments</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company name" /></div>
              <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Period</Label>
                <Select value={formData.period} onValueChange={(v) => setFormData({...formData, period: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1">Q1</SelectItem>
                    <SelectItem value="Q2">Q2</SelectItem>
                    <SelectItem value="Q3">Q3</SelectItem>
                    <SelectItem value="Q4">Q4</SelectItem>
                    <SelectItem value="Annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Year</Label><Input value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} placeholder="2025" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Your name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <Separator />
            <Label className="text-base font-semibold">Objectives & Key Results</Label>

            {formData.objectives.map((obj, oIdx) => (
              <Card key={oIdx} className="p-4 bg-slate-50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 grid grid-cols-4 gap-3">
                    <Input value={obj.obj_id} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].obj_id=e.target.value; setFormData({...formData, objectives: o});}} placeholder="O1" className="w-20" />
                    <Input className="col-span-2" value={obj.objective} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].objective=e.target.value; setFormData({...formData, objectives: o});}} placeholder="Objective description" />
                    <Input value={obj.owner} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].owner=e.target.value; setFormData({...formData, objectives: o});}} placeholder="Owner" />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, objectives: formData.objectives.filter((_, i) => i !== oIdx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Select value={obj.status} onValueChange={(v) => {const o=[...formData.objectives]; o[oIdx].status=v; setFormData({...formData, objectives: o});}}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not Started">Not Started</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="At Risk">At Risk</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Label className="whitespace-nowrap">Progress:</Label>
                    <Input type="number" min="0" max="100" value={obj.progress} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].progress=parseInt(e.target.value)||0; setFormData({...formData, objectives: o});}} className="w-20" />
                    <span>%</span>
                  </div>
                </div>
                <Label className="text-sm font-medium mb-2 block">Key Results</Label>
                {obj.key_results.map((kr, kIdx) => (
                  <div key={kIdx} className="grid grid-cols-7 gap-2 mb-2">
                    <Input value={kr.kr_id} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].key_results[kIdx].kr_id=e.target.value; setFormData({...formData, objectives: o});}} placeholder="KR1.1" className="text-sm" />
                    <Input className="col-span-2" value={kr.description} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].key_results[kIdx].description=e.target.value; setFormData({...formData, objectives: o});}} placeholder="Key result description" />
                    <Input value={kr.target_value} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].key_results[kIdx].target_value=e.target.value; setFormData({...formData, objectives: o});}} placeholder="Target" />
                    <Input value={kr.current_value} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].key_results[kIdx].current_value=e.target.value; setFormData({...formData, objectives: o});}} placeholder="Current" />
                    <Input type="date" value={kr.due_date} onChange={(e) => {const o=[...formData.objectives]; o[oIdx].key_results[kIdx].due_date=e.target.value; setFormData({...formData, objectives: o});}} />
                    <Button variant="ghost" size="icon" onClick={() => {const o=[...formData.objectives]; o[oIdx].key_results=o[oIdx].key_results.filter((_,i)=>i!==kIdx); setFormData({...formData, objectives: o});}}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addKeyResult(oIdx)}><Plus className="h-4 w-4 mr-1" /> Add Key Result</Button>
              </Card>
            ))}
            <Button variant="outline" onClick={addObjective}><Plus className="h-4 w-4 mr-2" /> Add Objective</Button>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.company_name} className="flex-1" size="lg">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== REUSABLE RAI SECTION COMPONENT ====================
const RAISection = ({ formData, setFormData }) => {
  const addRAI = () => {
    setFormData({...formData, rai_assignments: [...(formData.rai_assignments || []), {task: '', responsible: '', accountable: '', informed: '', due_date: '', status: 'Pending'}]});
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold">RAI Assignments (Responsible, Accountable, Informed)</Label>
      {(formData.rai_assignments || []).map((rai, idx) => (
        <div key={idx} className="grid grid-cols-7 gap-2">
          <Input className="col-span-2" value={rai.task} onChange={(e) => {const r=[...(formData.rai_assignments||[])]; r[idx].task=e.target.value; setFormData({...formData, rai_assignments: r});}} placeholder="Task/Action" />
          <Input value={rai.responsible} onChange={(e) => {const r=[...(formData.rai_assignments||[])]; r[idx].responsible=e.target.value; setFormData({...formData, rai_assignments: r});}} placeholder="Responsible" />
          <Input value={rai.accountable} onChange={(e) => {const r=[...(formData.rai_assignments||[])]; r[idx].accountable=e.target.value; setFormData({...formData, rai_assignments: r});}} placeholder="Accountable" />
          <Input value={rai.informed} onChange={(e) => {const r=[...(formData.rai_assignments||[])]; r[idx].informed=e.target.value; setFormData({...formData, rai_assignments: r});}} placeholder="Informed" />
          <Input type="date" value={rai.due_date} onChange={(e) => {const r=[...(formData.rai_assignments||[])]; r[idx].due_date=e.target.value; setFormData({...formData, rai_assignments: r});}} />
          <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, rai_assignments: (formData.rai_assignments||[]).filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRAI}><Plus className="h-4 w-4 mr-2" /> Add RAI Assignment</Button>
    </div>
  );
};

// ==================== REUSABLE FOLLOWUPS SECTION COMPONENT ====================
const FollowupsSection = ({ formData, setFormData }) => {
  const addFollowup = () => {
    setFormData({...formData, followups: [...(formData.followups || []), {item: '', owner: '', due_date: '', priority: 'Medium', status: 'Pending'}]});
  };

  return (
    <div className="space-y-3">
      <Label className="text-base font-semibold">Follow-ups & Next Steps</Label>
      {(formData.followups || []).map((f, idx) => (
        <div key={idx} className="grid grid-cols-6 gap-2">
          <Input className="col-span-2" value={f.item} onChange={(e) => {const fs=[...(formData.followups||[])]; fs[idx].item=e.target.value; setFormData({...formData, followups: fs});}} placeholder="Follow-up item" />
          <Input value={f.owner} onChange={(e) => {const fs=[...(formData.followups||[])]; fs[idx].owner=e.target.value; setFormData({...formData, followups: fs});}} placeholder="Owner" />
          <Input type="date" value={f.due_date} onChange={(e) => {const fs=[...(formData.followups||[])]; fs[idx].due_date=e.target.value; setFormData({...formData, followups: fs});}} />
          <Select value={f.priority} onValueChange={(v) => {const fs=[...(formData.followups||[])]; fs[idx].priority=v; setFormData({...formData, followups: fs});}}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, followups: (formData.followups||[]).filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addFollowup}><Plus className="h-4 w-4 mr-2" /> Add Follow-up</Button>
    </div>
  );
};

// ==================== KPI DASHBOARD FORM ====================
const KPIDashboardForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    company_name: '', department: '', period: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    kpis: [{kpi_id: 'KPI-001', name: '', category: '', description: '', formula: '', target: '', actual: '', unit: '', frequency: 'Monthly', trend: '', status: 'On Track', owner: '', data_source: ''}],
    summary: '', rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'Acme Corp', department: 'Sales', period: 'January 2025', date: new Date().toISOString().split('T')[0], prepared_by: 'CFO',
    kpis: [
      {kpi_id: 'KPI-001', name: 'Monthly Revenue', category: 'Financial', description: 'Total monthly revenue', formula: 'Sum of all invoices', target: '$500,000', actual: '$480,000', unit: 'USD', frequency: 'Monthly', trend: 'Up', status: 'At Risk', owner: 'Sales Director', data_source: 'CRM'},
      {kpi_id: 'KPI-002', name: 'Customer Acquisition Cost', category: 'Financial', description: 'Cost to acquire new customer', formula: 'Marketing spend / New customers', target: '$150', actual: '$125', unit: 'USD', frequency: 'Monthly', trend: 'Down', status: 'On Track', owner: 'Marketing', data_source: 'Analytics'},
      {kpi_id: 'KPI-003', name: 'Customer Satisfaction', category: 'Customer', description: 'NPS Score', formula: '% Promoters - % Detractors', target: '50', actual: '55', unit: 'NPS', frequency: 'Quarterly', trend: 'Up', status: 'On Track', owner: 'Support', data_source: 'Survey'}
    ],
    summary: 'Overall KPIs trending positively with minor gaps in revenue targets.',
    rai_assignments: [{task: 'Investigate revenue gap', responsible: 'Sales Director', accountable: 'CFO', informed: 'CEO', due_date: '2025-01-31', status: 'In Progress'}],
    followups: []
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);
  
  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };
  
  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);
  
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.company_name ? `KPI Dashboard - ${dataToSave.company_name}` : "KPI Dashboard Draft",
        template_type: "KPI Dashboard",
        template_id: "kpi-dashboard",
        format: "XLSX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/kpi-dashboard?settings_id=${selectedSetting}`
        : `${API}/generate/kpi-dashboard`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `KPI_Dashboard_${formData.company_name || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `KPI Dashboard - ${formData.company_name || 'Document'}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "KPI Dashboard",
        templateId: "kpi-dashboard",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "kpi-dashboard", "KPI Dashboard", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("kpi-dashboard", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const addKPI = () => {
    const newId = `KPI-${String(formData.kpis.length + 1).padStart(3, '0')}`;
    setFormData({...formData, kpis: [...formData.kpis, {kpi_id: newId, name: '', category: '', description: '', formula: '', target: '', actual: '', unit: '', frequency: 'Monthly', trend: '', status: 'On Track', owner: '', data_source: ''}]});
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">KPI Dashboard</h1><p className="text-sm text-gray-500">Key Performance Indicators tracking</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Sales" /></div>
              <div><Label>Period</Label><Input value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="January 2025" /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Your name" /></div>
            </div>

            <Separator />
            <Label className="text-base font-semibold">KPI Metrics</Label>

            {formData.kpis.map((kpi, idx) => (
              <Card key={idx} className="p-4 bg-slate-50">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <Input value={kpi.kpi_id} onChange={(e) => {const k=[...formData.kpis]; k[idx].kpi_id=e.target.value; setFormData({...formData, kpis: k});}} placeholder="KPI-001" />
                  <Input className="col-span-2" value={kpi.name} onChange={(e) => {const k=[...formData.kpis]; k[idx].name=e.target.value; setFormData({...formData, kpis: k});}} placeholder="KPI Name" />
                  <Select value={kpi.category} onValueChange={(v) => {const k=[...formData.kpis]; k[idx].category=v; setFormData({...formData, kpis: k});}}>
                    <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Financial">Financial</SelectItem>
                      <SelectItem value="Customer">Customer</SelectItem>
                      <SelectItem value="Process">Process</SelectItem>
                      <SelectItem value="Learning">Learning</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-5 gap-3 mb-3">
                  <Input value={kpi.target} onChange={(e) => {const k=[...formData.kpis]; k[idx].target=e.target.value; setFormData({...formData, kpis: k});}} placeholder="Target" />
                  <Input value={kpi.actual} onChange={(e) => {const k=[...formData.kpis]; k[idx].actual=e.target.value; setFormData({...formData, kpis: k});}} placeholder="Actual" />
                  <Select value={kpi.trend} onValueChange={(v) => {const k=[...formData.kpis]; k[idx].trend=v; setFormData({...formData, kpis: k});}}>
                    <SelectTrigger><SelectValue placeholder="Trend" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Up">Up ↑</SelectItem>
                      <SelectItem value="Down">Down ↓</SelectItem>
                      <SelectItem value="Stable">Stable →</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={kpi.status} onValueChange={(v) => {const k=[...formData.kpis]; k[idx].status=v; setFormData({...formData, kpis: k});}}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On Track">On Track</SelectItem>
                      <SelectItem value="At Risk">At Risk</SelectItem>
                      <SelectItem value="Off Track">Off Track</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input value={kpi.owner} onChange={(e) => {const k=[...formData.kpis]; k[idx].owner=e.target.value; setFormData({...formData, kpis: k});}} placeholder="Owner" />
                </div>
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setFormData({...formData, kpis: formData.kpis.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500 mr-1" /> Remove</Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={addKPI}><Plus className="h-4 w-4 mr-2" /> Add KPI</Button>

            <div><Label>Summary</Label><Textarea value={formData.summary} onChange={(e) => setFormData({...formData, summary: e.target.value})} placeholder="Overall KPI summary..." rows={3} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.company_name} className="flex-1" size="lg">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== BUDGET PLANNING FORM ====================
const BudgetPlanningForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    company_name: '', department: '', fiscal_year: new Date().getFullYear().toString(), date: new Date().toISOString().split('T')[0],
    prepared_by: '', approved_by: '', currency: 'USD',
    budget_items: [{category: '', subcategory: '', description: '', q1_budget: 0, q2_budget: 0, q3_budget: 0, q4_budget: 0, q1_actual: 0, q2_actual: 0, q3_actual: 0, q4_actual: 0, owner: ''}],
    assumptions: [''], risks: [''], rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'Acme Corp', department: 'Engineering', fiscal_year: '2025', date: new Date().toISOString().split('T')[0],
    prepared_by: 'CFO', approved_by: 'CEO', currency: 'USD',
    budget_items: [
      {category: 'Personnel', subcategory: 'Salaries', description: 'Engineering team salaries', q1_budget: 250000, q2_budget: 250000, q3_budget: 275000, q4_budget: 275000, q1_actual: 248000, q2_actual: 0, q3_actual: 0, q4_actual: 0, owner: 'HR'},
      {category: 'Infrastructure', subcategory: 'Cloud', description: 'AWS/GCP hosting costs', q1_budget: 50000, q2_budget: 55000, q3_budget: 60000, q4_budget: 65000, q1_actual: 52000, q2_actual: 0, q3_actual: 0, q4_actual: 0, owner: 'DevOps'},
      {category: 'Software', subcategory: 'Licenses', description: 'Tool subscriptions', q1_budget: 15000, q2_budget: 15000, q3_budget: 15000, q4_budget: 15000, q1_actual: 14500, q2_actual: 0, q3_actual: 0, q4_actual: 0, owner: 'IT'}
    ],
    assumptions: ['10% headcount growth in Q3', 'No major infrastructure changes', '5% inflation adjustment'],
    risks: ['Currency fluctuation risk', 'Potential vendor price increases'],
    rai_assignments: [{task: 'Monthly budget review', responsible: 'Finance', accountable: 'CFO', informed: 'Department Heads', due_date: '2025-01-31', status: 'Pending'}],
    followups: [{item: 'Q1 budget reconciliation', owner: 'Finance', due_date: '2025-04-15', priority: 'High', status: 'Pending'}]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);
  
  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };
  
  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);
  
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.department ? `Budget - ${dataToSave.department} ${dataToSave.fiscal_year}` : "Budget Draft",
        template_type: "Budget Planning",
        template_id: "budget-planning",
        format: "XLSX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/budget-planning?settings_id=${selectedSetting}`
        : `${API}/generate/budget-planning`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Budget_${formData.department || 'Document'}_${formData.fiscal_year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `Budget - ${formData.department || 'Document'} ${formData.fiscal_year}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "Budget Planning",
        templateId: "budget-planning",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "budget-planning", "Budget Planning", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("budget-planning", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Budget Planning Sheet</h1><p className="text-sm text-gray-500">Annual/quarterly budget allocation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Budget Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department *</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Fiscal Year</Label><Input value={formData.fiscal_year} onChange={(e) => setFormData({...formData, fiscal_year: e.target.value})} placeholder="2025" /></div>
              <div><Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({...formData, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />
            <Label className="text-base font-semibold">Budget Line Items</Label>

            {formData.budget_items.map((item, idx) => (
              <Card key={idx} className="p-4 bg-slate-50">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <Input value={item.category} onChange={(e) => {const b=[...formData.budget_items]; b[idx].category=e.target.value; setFormData({...formData, budget_items: b});}} placeholder="Category" />
                  <Input value={item.subcategory} onChange={(e) => {const b=[...formData.budget_items]; b[idx].subcategory=e.target.value; setFormData({...formData, budget_items: b});}} placeholder="Subcategory" />
                  <Input className="col-span-2" value={item.description} onChange={(e) => {const b=[...formData.budget_items]; b[idx].description=e.target.value; setFormData({...formData, budget_items: b});}} placeholder="Description" />
                </div>
                <div className="grid grid-cols-5 gap-2 mb-2">
                  <Label className="text-xs text-gray-500 col-span-5">Budget by Quarter:</Label>
                  <Input type="number" value={item.q1_budget} onChange={(e) => {const b=[...formData.budget_items]; b[idx].q1_budget=parseFloat(e.target.value)||0; setFormData({...formData, budget_items: b});}} placeholder="Q1 Budget" />
                  <Input type="number" value={item.q2_budget} onChange={(e) => {const b=[...formData.budget_items]; b[idx].q2_budget=parseFloat(e.target.value)||0; setFormData({...formData, budget_items: b});}} placeholder="Q2 Budget" />
                  <Input type="number" value={item.q3_budget} onChange={(e) => {const b=[...formData.budget_items]; b[idx].q3_budget=parseFloat(e.target.value)||0; setFormData({...formData, budget_items: b});}} placeholder="Q3 Budget" />
                  <Input type="number" value={item.q4_budget} onChange={(e) => {const b=[...formData.budget_items]; b[idx].q4_budget=parseFloat(e.target.value)||0; setFormData({...formData, budget_items: b});}} placeholder="Q4 Budget" />
                  <Input value={item.owner} onChange={(e) => {const b=[...formData.budget_items]; b[idx].owner=e.target.value; setFormData({...formData, budget_items: b});}} placeholder="Owner" />
                </div>
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setFormData({...formData, budget_items: formData.budget_items.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500 mr-1" /> Remove</Button>
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={() => setFormData({...formData, budget_items: [...formData.budget_items, {category: '', subcategory: '', description: '', q1_budget: 0, q2_budget: 0, q3_budget: 0, q4_budget: 0, q1_actual: 0, q2_actual: 0, q3_actual: 0, q4_actual: 0, owner: ''}]})}><Plus className="h-4 w-4 mr-2" /> Add Line Item</Button>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.company_name} className="flex-1" size="lg">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== TECHNICAL DESIGN FORM ====================
const TechnicalDesignForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    project_name: '', document_version: '1.0', date: new Date().toISOString().split('T')[0], prepared_by: '', reviewed_by: '', approved_by: '', status: 'Draft',
    executive_summary: '', problem_statement: '', goals: [''], non_goals: [''],
    system_overview: '', components: [{name: '', description: '', technology: '', dependencies: [], security_considerations: ''}],
    technology_stack: {}, infrastructure: '', security: '', scalability: '',
    milestones: [{milestone: '', target_date: '', status: 'Planned'}], risks: [{risk: '', impact: '', mitigation: ''}],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'User Authentication Module', document_version: '1.0', date: new Date().toISOString().split('T')[0],
    prepared_by: 'Tech Lead', reviewed_by: 'Architect', approved_by: 'CTO', status: 'Draft',
    executive_summary: 'This document outlines the technical design for the new user authentication module that will support OAuth2, MFA, and SSO capabilities.',
    problem_statement: 'Current authentication system lacks OAuth2 support and multi-factor authentication, limiting integration capabilities and security posture.',
    goals: ['Implement OAuth2 authentication flow', 'Add MFA support (TOTP, SMS)', 'Enable SSO with corporate IdP', 'Maintain backward compatibility'],
    non_goals: ['Biometric authentication (Phase 2)', 'Hardware key support'],
    system_overview: 'The authentication module will be built as a microservice with its own database, integrating with the main application via REST APIs and event-driven messaging.',
    components: [
      {name: 'Auth Service', description: 'Core authentication logic', technology: 'FastAPI', dependencies: ['PostgreSQL', 'Redis'], security_considerations: 'Token encryption, rate limiting'},
      {name: 'Token Manager', description: 'JWT token issuance and validation', technology: 'Python', dependencies: ['Auth Service'], security_considerations: 'Short-lived tokens, secure refresh'}
    ],
    technology_stack: {Backend: 'FastAPI', Database: 'PostgreSQL', Cache: 'Redis', Queue: 'RabbitMQ'},
    infrastructure: 'Deployed on Kubernetes with horizontal pod autoscaling. Database on managed RDS with read replicas.',
    security: 'All tokens encrypted at rest. TLS 1.3 for transit. OWASP compliance. Regular security audits.',
    scalability: 'Designed to handle 10,000 auth requests/second. Stateless architecture for easy horizontal scaling.',
    milestones: [
      {milestone: 'Design Approval', target_date: '2025-01-20', status: 'In Progress'},
      {milestone: 'MVP Development', target_date: '2025-02-15', status: 'Planned'},
      {milestone: 'Security Audit', target_date: '2025-02-28', status: 'Planned'}
    ],
    risks: [
      {risk: 'Third-party OAuth provider downtime', impact: 'High', mitigation: 'Implement fallback to email-based auth'},
      {risk: 'Integration complexity', impact: 'Medium', mitigation: 'Thorough API documentation and staged rollout'}
    ],
    rai_assignments: [{task: 'Complete API design', responsible: 'Tech Lead', accountable: 'Architect', informed: 'Frontend Team', due_date: '2025-01-25', status: 'In Progress'}],
    followups: [{item: 'Security review meeting', owner: 'Security Team', due_date: '2025-01-30', priority: 'High', status: 'Pending'}]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);
  
  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };
  
  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);
  
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.project_name ? `TDD - ${dataToSave.project_name}` : "Technical Design Draft",
        template_type: "Technical Design",
        template_id: "technical-design",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/technical-design?settings_id=${selectedSetting}`
        : `${API}/generate/technical-design`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `TDD_${formData.project_name || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `Technical Design - ${formData.project_name || 'Document'}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "Technical Design",
        templateId: "technical-design",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "technical-design", "Technical Design", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("technical-design", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Technical Design Document</h1><p className="text-sm text-gray-500">Architecture & design specifications</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Version</Label><Input value={formData.document_version} onChange={(e) => setFormData({...formData, document_version: e.target.value})} placeholder="1.0" /></div>
              <div><Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="In Review">In Review</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Author" /></div>
              <div><Label>Reviewed By</Label><Input value={formData.reviewed_by} onChange={(e) => setFormData({...formData, reviewed_by: e.target.value})} placeholder="Reviewer" /></div>
              <div><Label>Approved By</Label><Input value={formData.approved_by} onChange={(e) => setFormData({...formData, approved_by: e.target.value})} placeholder="Approver" /></div>
            </div>

            <Separator />
            <div><Label>Executive Summary</Label><Textarea value={formData.executive_summary} onChange={(e) => setFormData({...formData, executive_summary: e.target.value})} placeholder="Brief overview of the design..." rows={3} /></div>
            <div><Label>Problem Statement</Label><Textarea value={formData.problem_statement} onChange={(e) => setFormData({...formData, problem_statement: e.target.value})} placeholder="What problem does this solve?" rows={3} /></div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="font-semibold">Goals</Label>
                {formData.goals.map((goal, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={goal} onChange={(e) => {const g=[...formData.goals]; g[idx]=e.target.value; setFormData({...formData, goals: g});}} placeholder="Goal" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, goals: formData.goals.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setFormData({...formData, goals: [...formData.goals, '']})}><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              <div>
                <Label className="font-semibold">Non-Goals</Label>
                {formData.non_goals.map((ng, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={ng} onChange={(e) => {const n=[...formData.non_goals]; n[idx]=e.target.value; setFormData({...formData, non_goals: n});}} placeholder="Non-goal" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, non_goals: formData.non_goals.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setFormData({...formData, non_goals: [...formData.non_goals, '']})}><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <div><Label>System Overview</Label><Textarea value={formData.system_overview} onChange={(e) => setFormData({...formData, system_overview: e.target.value})} placeholder="High-level system description..." rows={3} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.project_name} className="flex-1" size="lg">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== EXECUTIVE SUMMARY FORM ====================
const ExecutiveSummaryForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');
  
  // Refs to track latest values for auto-save
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);

  const getEmptyForm = () => ({
    company_name: '', report_title: '', period: '', date: new Date().toISOString().split('T')[0], prepared_by: '', prepared_for: '',
    executive_overview: '', key_highlights: [''], key_concerns: [''], overall_status: 'On Track',
    key_metrics: [{name: '', category: '', current_value: '', target_value: '', trend: '', status: 'On Track'}],
    project_status: [{project: '', status: '', progress: '', notes: ''}],
    recommendations: [''], decisions_needed: [''], next_steps: [''],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'Acme Corp', report_title: 'Q1 2025 Executive Summary', period: 'Q1 2025', date: new Date().toISOString().split('T')[0],
    prepared_by: 'COO', prepared_for: 'Board of Directors', executive_overview: 'Q1 showed strong progress across all business units with revenue up 15% YoY. Engineering delivered on key milestones while maintaining quality metrics.',
    key_highlights: ['Revenue exceeded target by 8%', 'Launched 3 new product features', 'Customer NPS increased to 65', 'Reduced operational costs by 12%'],
    key_concerns: ['Supply chain delays affecting Q2 projections', 'Key talent retention in engineering'],
    overall_status: 'On Track',
    key_metrics: [
      {name: 'Revenue', category: 'Financial', current_value: '$12.5M', target_value: '$12M', trend: 'Up', status: 'On Track'},
      {name: 'Customer Count', category: 'Growth', current_value: '2,450', target_value: '2,500', trend: 'Up', status: 'At Risk'},
      {name: 'Employee Satisfaction', category: 'People', current_value: '78%', target_value: '80%', trend: 'Stable', status: 'On Track'}
    ],
    project_status: [
      {project: 'Platform V2', status: 'On Track', progress: '65%', notes: 'Launch planned for March'},
      {project: 'Mobile App', status: 'At Risk', progress: '40%', notes: 'Resource constraints'}
    ],
    recommendations: ['Accelerate mobile app development with additional resources', 'Implement retention program for engineering team'],
    decisions_needed: ['Approve Q2 budget increase for mobile project', 'Sign off on new vendor contract'],
    next_steps: ['Complete Q1 financial close', 'Begin Q2 planning cycle', 'Conduct engineering team offsites'],
    rai_assignments: [{task: 'Prepare detailed Q2 plan', responsible: 'CFO', accountable: 'CEO', informed: 'Board', due_date: '2025-02-15', status: 'Pending'}],
    followups: []
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);
  
  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);
  
  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };
  
  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);
  
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = {
        name: dataToSave.report_title ? `Executive Summary - ${dataToSave.report_title}` : "Executive Summary Draft",
        template_type: "Executive Summary",
        template_id: "executive-summary",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) {
      alert("Draft saved successfully!");
    } else {
      alert("Error saving draft.");
    }
  };

  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const url = selectedSetting && selectedSetting !== "none" 
        ? `${API}/generate/executive-summary?settings_id=${selectedSetting}`
        : `${API}/generate/executive-summary`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Executive_Summary_${formData.company_name || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: `Executive Summary - ${formData.company_name || 'Document'}`,
        formData,
        settings: settings?.find(s => s.id === selectedSetting) || {},
        templateType: "Executive Summary",
        templateId: "executive-summary",
        createdBy: user?.email,
        selectedSetting,
        allSettings: settings
      });
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating PDF.");
    }
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    exportToDCTM(formData, "executive-summary", "Executive Summary", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate("executive-summary", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`);
      if (!proceed) return;
    }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Executive Summary</h1><p className="text-sm text-gray-500">High-level status report for leadership</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())}><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }}><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Report Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Report Title</Label><Input value={formData.report_title} onChange={(e) => setFormData({...formData, report_title: e.target.value})} placeholder="Q1 Executive Summary" /></div>
              <div><Label>Period</Label><Input value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="Q1 2025" /></div>
              <div><Label>Overall Status</Label>
                <Select value={formData.overall_status} onValueChange={(v) => setFormData({...formData, overall_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="On Track">On Track</SelectItem>
                    <SelectItem value="At Risk">At Risk</SelectItem>
                    <SelectItem value="Off Track">Off Track</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Your name/role" /></div>
              <div><Label>Prepared For</Label><Input value={formData.prepared_for} onChange={(e) => setFormData({...formData, prepared_for: e.target.value})} placeholder="Board of Directors" /></div>
            </div>

            <div><Label>Executive Overview</Label><Textarea value={formData.executive_overview} onChange={(e) => setFormData({...formData, executive_overview: e.target.value})} placeholder="High-level summary..." rows={4} /></div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="font-semibold text-green-700">Key Highlights ✓</Label>
                {formData.key_highlights.map((h, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={h} onChange={(e) => {const hl=[...formData.key_highlights]; hl[idx]=e.target.value; setFormData({...formData, key_highlights: hl});}} placeholder="Highlight" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, key_highlights: formData.key_highlights.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setFormData({...formData, key_highlights: [...formData.key_highlights, '']})}><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
              <div>
                <Label className="font-semibold text-red-700">Key Concerns ⚠</Label>
                {formData.key_concerns.map((c, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={c} onChange={(e) => {const kc=[...formData.key_concerns]; kc[idx]=e.target.value; setFormData({...formData, key_concerns: kc});}} placeholder="Concern" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, key_concerns: formData.key_concerns.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setFormData({...formData, key_concerns: [...formData.key_concerns, '']})}><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
              <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || !formData.company_name} className="flex-1" size="lg">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
              </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== SIMPLE FORM COMPONENT (for remaining templates) ====================
const SimpleForm = ({ title, endpoint, format, fields }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const searchParams = new URLSearchParams(window.location.search);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => fields.reduce((acc, f) => ({ ...acc, [f]: f.includes('date') ? new Date().toISOString().split('T')[0] : '' }), { rai_assignments: [], followups: [], version: '1.0' });
  const [formData, setFormData] = useState(getEmptyForm());

  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { savedTemplateIdRef.current = savedTemplateId; }, [savedTemplateId]);
  useEffect(() => { if (activeSettings?.id) setSelectedSetting(activeSettings.id); }, [activeSettings]);

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      const loadSavedTemplate = async () => {
        try {
          const headers = user?.email ? { 'X-User-Email': user.email } : {};
          const response = await axios.get(`${API}/saved-templates/${editingSavedId}`, { headers });
          if (response.data?.data) {
            setFormData(response.data.data);
            setSavedTemplateId(editingSavedId);
          }
        } catch (error) { console.error("Error loading saved template:", error); }
      };
      loadSavedTemplate();
    }
  }, [editingSavedId, user?.email]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) saveDraftInternal(currentFormData, currentSavedId, true);
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) { clearInterval(autoSaveIntervalRef.current); autoSaveIntervalRef.current = null; }
    }
    return () => { if (autoSaveIntervalRef.current) { clearInterval(autoSaveIntervalRef.current); autoSaveIntervalRef.current = null; } };
  }, [autoSave, user?.email]);

  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const payload = { name: dataToSave.project_name || dataToSave.title || `${title} Draft`, template_type: title, template_id: endpoint, format, data: dataToSave };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        if (response.data?.version) setFormData(prev => ({ ...prev, version: response.data.version }));
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) setSavedTemplateId(response.data.id);
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) { console.error("Error saving draft:", error); if (!isAutoSave) setSaving(false); return false; }
  };
  
  const handleSaveDraft = async () => {
    const success = await saveDraftInternal(formData, savedTemplateId, false);
    if (success) alert("Draft saved successfully!"); else alert("Error saving draft.");
  };

  // DCTM Import/Export handlers
  const handleExportDCTM = () => { exportToDCTM(formData, endpoint, title, user?.email || ''); };
  const handleImportDCTM = async (file) => {
    const result = await importFromDCTM(file);
    if (!result.success) { alert(result.error); return; }
    const validation = validateDCTMTemplate(endpoint, result.meta);
    if (validation.warning) { const proceed = window.confirm(`Warning: ${validation.warning}\n\nDo you want to continue importing?`); if (!proceed) return; }
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    alert(`Document imported successfully!\nOriginal version: ${result.meta.documentVersion}\nNew version: ${newVersion}`);
  };

  const handleDownloadPDF = async () => {
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({ title: formData.title || formData.project_name || title, formData, settings: settings?.find(s => s.id === selectedSetting) || {}, templateType: title, templateId: endpoint, createdBy: user?.email, selectedSetting, allSettings: settings });
    } catch (error) { console.error("Error:", error); alert("Error generating PDF."); }
    setPdfLoading(false);
  };

  const handleDownload = async () => {
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/${endpoint}`;
      if (selectedSetting && selectedSetting !== "none") url += `?settings_id=${selectedSetting}`;
      const response = await axios.post(url, formData, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `${title.replace(/\s+/g, '_')}.${format.toLowerCase()}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error(error); alert("Error generating document. Please fill required fields."); }
    setLoading(false);
  };

  const fieldLabels = {
    company_name: 'Company Name', project_name: 'Project Name', product_name: 'Product Name', api_name: 'API Name',
    department: 'Department', period: 'Period', date: 'Date', prepared_by: 'Prepared By', reported_by: 'Reported By',
    fiscal_year: 'Fiscal Year', version: 'Version', release_date: 'Release Date', release_type: 'Release Type',
    currency: 'Currency', analysis_period: 'Analysis Period', meeting_date: 'Meeting Date', meeting_type: 'Meeting Type',
    roadmap_name: 'Roadmap Name', forecast_name: 'Forecast Name', runbook_name: 'Runbook Name', system_name: 'System Name',
    environment: 'Environment', base_url: 'Base URL', incident_id: 'Incident ID', incident_title: 'Incident Title',
    title: 'Title', severity: 'Severity', status: 'Status', facilitator: 'Facilitator', quarter: 'Quarter', year: 'Year'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">{title}</h1><p className="text-sm text-gray-500">Generate {format} document</p></div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]"><Building2 className="h-4 w-4 mr-2" /><SelectValue placeholder="Header/Footer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Document Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {fields.map((field) => (
                <div key={field}>
                  <Label>{fieldLabels[field] || field}</Label>
                  <Input 
                    type={field.includes('date') ? 'date' : 'text'}
                    value={formData[field] || ''} 
                    onChange={(e) => setFormData({...formData, [field]: e.target.value})} 
                    placeholder={fieldLabels[field] || field} 
                  />
                </div>
              ))}
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={handleSaveDraft} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                  <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
                </Button>
                <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                  <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : `Download ${format}`}
                </Button>
                <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
                </Button>
                <input type="file" ref={dctmFileInputRef} accept=".dctm" onChange={(e) => { if (e.target.files?.[0]) { handleImportDCTM(e.target.files[0]); e.target.value = ''; } }} className="hidden" data-testid="dctm-file-input" />
                <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                  <FileUp className="h-5 w-5 mr-2" /> Import
                </Button>
                <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                  <FileDown className="h-5 w-5 mr-2" /> Export
                </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">{loading ? "Generating document..." : "Generating PDF document..."}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// ==================== SAVED TEMPLATES PAGE ====================
const SavedTemplatesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = user?.email ? { 'X-User-Email': user.email } : {};

  useEffect(() => {
    if (user?.email) {
      fetchSavedTemplates();
    }
  }, [user?.email]);

  const fetchSavedTemplates = async () => {
    try {
      const response = await axios.get(`${API}/saved-templates`, { headers });
      setSavedTemplates(response.data);
    } catch (error) {
      console.error("Error fetching saved templates:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this saved template?")) return;
    try {
      await axios.delete(`${API}/saved-templates/${id}`, { headers });
      fetchSavedTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
    }
  };

  const handleDownload = async (template) => {
    // Export as .dctm file
    const dctmDocument = {
      _dctm: {
        version: "1.0",
        format: "dctm",
        generator: "Document Templates",
        exportedAt: new Date().toISOString()
      },
      meta: {
        templateId: template.template_id,
        templateType: template.template_type,
        createdBy: user?.email || '',
        documentVersion: template.data?.version || "1.0",
        name: template.name
      },
      data: template.data
    };

    const jsonString = JSON.stringify(dctmDocument, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const docName = template.name.replace(/[^a-zA-Z0-9]/g, '_');
    const fileName = `${docName}_v${dctmDocument.meta.documentVersion}.dctm`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Draft</h1>
                <p className="text-sm text-gray-500">View and manage your draft documents</p>
              </div>
            </div>
          </div>
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Documents are saved in your workspace and are not centralized. They are only available on the device and browser where they were saved.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : savedTemplates.length === 0 ? (
          <Card className="p-12 text-center">
            <ClipboardList className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Drafts</h3>
            <p className="text-gray-500 mb-4">
              When you save a document as draft, it will appear here for easy access and editing.
            </p>
            <Link to="/">
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Create New Document
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid gap-4">
            {savedTemplates.map((template) => (
              <Card key={template.id}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        {template.format === "DOCX" ? <FileText className="h-6 w-6 text-blue-600" /> : <FileSpreadsheet className="h-6 w-6 text-green-600" />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{template.name}</h3>
                        <p className="text-sm text-gray-500">
                          {template.template_type} • {template.format} • Saved {new Date(template.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/template/${template.template_id}?saved=${template.id}`}>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                      </Link>
                      <Button variant="outline" size="sm" onClick={() => handleDownload(template)}>
                        <Download className="h-4 w-4 mr-1" /> Download
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(template.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ==================== API DOCS PAGE ====================
const APIDocsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  const endpoints = [
    { method: "GET", path: "/api/settings", description: "Get all document settings" },
    { method: "POST", path: "/api/settings", description: "Create new document setting" },
    { method: "GET", path: "/api/settings/default/current", description: "Get current default setting" },
    { method: "POST", path: "/api/generate/{template-id}", description: "Generate a document" },
    { method: "GET", path: "/api/saved-templates", description: "List all saved templates" },
    { method: "POST", path: "/api/saved-templates", description: "Save a template draft" },
    { method: "GET", path: "/api/saved-templates/{id}", description: "Get a specific saved template" },
    { method: "DELETE", path: "/api/saved-templates/{id}", description: "Delete a saved template" },
    { method: "POST", path: "/api/saved-templates/{id}/download", description: "Download saved template" }
  ];

  const templateTypes = [
    { category: "Strategy & OKRs", templates: ["okr", "kpi-dashboard", "raci-matrix", "strategic-roadmap"] },
    { category: "Finance & Budgeting", templates: ["budget-planning", "financial-forecast", "roi-analysis", "vendor-comparison"] },
    { category: "Resource & Capacity", templates: ["capacity-planning", "resource-allocation", "hiring-plan", "skills-matrix"] },
    { category: "Engineering & DevOps", templates: ["technical-design", "release-notes", "incident-report", "post-mortem", "runbook", "api-documentation"] },
    { category: "Executive Reports", templates: ["executive-summary", "board-report", "qbr", "investor-update"] },
    { category: "Core Development", templates: ["ba-requirements", "software-proposal", "product-tracksheet", "cost-calculation", "bug-log", "test-cases", "code-review"] },
    { category: "Meetings & Communication", templates: ["minutes-of-meeting", "meeting-agenda", "action-item-tracker", "stakeholder-communication-log"] },
    { category: "Scrum / Agile", templates: ["sprint-planning", "daily-standup", "sprint-retrospective", "product-backlog", "burndown-chart", "definition-of-done", "definition-of-ready"] },
    { category: "Project Governance", templates: ["change-request", "decision-log", "issue-risk-register", "escalation-matrix"] },
    { category: "Delivery & Closure", templates: ["go-live-checklist", "project-closure", "lessons-learned", "client-feedback"] }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">API Documentation</h1>
                <p className="text-sm text-gray-500">Public API for document generation</p>
              </div>
            </div>
            <Badge variant="outline" className="font-mono">v2.1.2</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
            <TabsTrigger value="examples">Examples</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>Getting Started</CardTitle>
                <CardDescription>Learn how to use the Document Templates API</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Base URL</h3>
                  <code className="bg-gray-100 px-3 py-2 rounded block font-mono text-sm">
                    {BACKEND_URL}/api
                  </code>
                </div>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">Authentication</h3>
                  <p className="text-gray-600">
                    Currently, the API is open for public use. API key authentication will be added in a future version.
                  </p>
                </div>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">Content Types</h3>
                  <p className="text-gray-600 mb-2">Request bodies should be sent as JSON:</p>
                  <code className="bg-gray-100 px-3 py-2 rounded block font-mono text-sm">
                    Content-Type: application/json
                  </code>
                </div>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">Response Formats</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Document generation endpoints return binary file data (DOCX or XLSX)</li>
                    <li>Data endpoints return JSON</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="endpoints">
            <Card>
              <CardHeader>
                <CardTitle>API Endpoints</CardTitle>
                <CardDescription>Available API endpoints for document generation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {endpoints.map((ep, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <Badge className={ep.method === "GET" ? "bg-green-100 text-green-800" : ep.method === "POST" ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"}>
                        {ep.method}
                      </Badge>
                      <code className="font-mono text-sm flex-1">{ep.path}</code>
                      <span className="text-gray-500 text-sm">{ep.description}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <Card>
              <CardHeader>
                <CardTitle>Available Templates</CardTitle>
                <CardDescription>Template IDs for document generation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {templateTypes.map((cat, index) => (
                    <div key={index}>
                      <h3 className="font-semibold text-gray-900 mb-2">{cat.category}</h3>
                      <div className="flex flex-wrap gap-2">
                        {cat.templates.map((t) => (
                          <code key={t} className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{t}</code>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="examples">
            <Card>
              <CardHeader>
                <CardTitle>Code Examples</CardTitle>
                <CardDescription>Example API calls using cURL and JavaScript</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Generate OKR Template (cURL)</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST "${BACKEND_URL}/api/generate/okr" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Q1 2025 OKRs",
    "period": "Q1 2025",
    "department": "Engineering",
    "objectives": [
      {
        "objective": "Improve Platform Reliability",
        "key_results": [
          {"key_result": "Achieve 99.9% uptime", "target": "99.9%", "current": "99.5%", "progress": 50}
        ]
      }
    ]
  }' \\
  -o okr_template.xlsx`}</pre>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-2">Generate Document (JavaScript)</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`const generateDocument = async (templateId, data) => {
  const response = await fetch(\`${BACKEND_URL}/api/generate/\${templateId}\`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = \`\${templateId}.docx\`;
  a.click();
};

// Usage
generateDocument('ba-requirements', {
  project_name: 'My Project',
  client_name: 'Client Corp',
  // ... other fields
});`}</pre>
                </div>

                <Separator />

                <div>
                  <h3 className="font-semibold mb-2">Save Template Draft</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST "${BACKEND_URL}/api/saved-templates" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My OKR Draft",
    "template_type": "OKR Template",
    "template_id": "okr",
    "format": "XLSX",
    "data": {
      "title": "Q1 2025 OKRs",
      "objectives": []
    }
  }'`}</pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ==================== COMPANY SETTINGS PAGE ====================
// Template Form Component - defined outside to prevent re-creation on each render
const CompanyTemplateForm = ({ template, setTemplate, onSubmit, submitLabel, onCancel }) => {
  const [logoPreview, setLogoPreview] = useState(template.logo_url || '');
  const fileInputRef = React.useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setTemplate({ ...template, logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview('');
    setTemplate({ ...template, logo_url: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Template Name */}
      <div>
        <Label>Template Name *</Label>
        <Input 
          value={template.name} 
          onChange={(e) => setTemplate({...template, name: e.target.value})} 
          placeholder="e.g., VROZART" 
          required 
          className="mt-1"
        />
      </div>

      {/* Company Information Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Building2 className="h-4 w-4" />
          Company Information
        </div>
        
        {/* Logo Upload */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <Label className="text-sm">Company Logo</Label>
          <div className="flex items-center gap-4 mt-2">
            {logoPreview ? (
              <div className="relative">
                <img src={logoPreview} alt="Logo" className="h-16 max-w-[200px] object-contain" />
                <button 
                  type="button" 
                  onClick={removeLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="h-16 w-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 text-xs">
                No Logo
              </div>
            )}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                {logoPreview ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-gray-500 mt-1">PNG, JPEG, GIF or WebP. Max 2MB.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Company Name</Label>
            <Input 
              value={template.company_name} 
              onChange={(e) => setTemplate({...template, company_name: e.target.value})} 
              placeholder="Vrozart Group" 
              className="mt-1"
            />
          </div>
          <div>
            <Label>Tagline</Label>
            <Input 
              value={template.tagline || ''} 
              onChange={(e) => setTemplate({...template, tagline: e.target.value})} 
              placeholder="Driving growth with purpose, precision, and impact" 
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Email</Label>
            <Input 
              value={template.company_email} 
              onChange={(e) => setTemplate({...template, company_email: e.target.value})} 
              placeholder="info@company.com" 
              className="mt-1"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              value={template.company_phone} 
              onChange={(e) => setTemplate({...template, company_phone: e.target.value})} 
              placeholder="+91 88008 45946" 
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label>Website</Label>
          <Input 
            value={template.company_website} 
            onChange={(e) => setTemplate({...template, company_website: e.target.value})} 
            placeholder="www.company.com" 
            className="mt-1"
          />
        </div>

        <div>
          <Label>Address</Label>
          <Textarea 
            value={template.company_address} 
            onChange={(e) => setTemplate({...template, company_address: e.target.value})} 
            placeholder="5635, DLF Phase IV, Gurugram, Haryana 122009" 
            rows={2}
            className="mt-1"
          />
        </div>
      </div>

      {/* Regional Settings Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">Regional Settings</h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Currency</Label>
            <Select 
              value={template.currency || 'USD'} 
              onValueChange={(value) => setTemplate({...template, currency: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Timezone</Label>
            <Select 
              value={template.timezone || 'UTC'} 
              onValueChange={(value) => setTemplate({...template, timezone: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Date Format</Label>
            <Select 
              value={template.date_format || 'MMM DD, YYYY'} 
              onValueChange={(value) => setTemplate({...template, date_format: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select date format" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMAT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Time Format</Label>
            <Select 
              value={template.time_format || '12h'} 
              onValueChange={(value) => setTemplate({...template, time_format: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select time format" />
              </SelectTrigger>
              <SelectContent>
                {TIME_FORMAT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Document Header & Footer Section */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700">Document Header & Footer</h4>
        
        <div>
          <Label>Header Text</Label>
          <Input 
            value={template.header_text} 
            onChange={(e) => setTemplate({...template, header_text: e.target.value})} 
            placeholder="Driving growth with purpose, precision, and impact" 
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">If empty, company name will be used</p>
        </div>

        <div>
          <Label>Footer Line 1 (Additional Info)</Label>
          <Input 
            value={template.footer_line1 || ''} 
            onChange={(e) => setTemplate({...template, footer_line1: e.target.value})} 
            placeholder="123 Business Street, City, State 12345 | info@company.com | +1 234 567 8900" 
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">This line appears above the contact information</p>
        </div>

        <div>
          <Label>Footer Line 2 (Contact Info)</Label>
          <Input 
            value={template.footer_text} 
            onChange={(e) => setTemplate({...template, footer_text: e.target.value})} 
            placeholder="Confidential, all rights reserved © 2026" 
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">Email, phone, and website will be appended automatically</p>
        </div>
      </div>

      {/* Default Toggle */}
      <div className="flex items-center gap-2 pt-2">
        <Switch 
          checked={template.is_default} 
          onCheckedChange={(checked) => setTemplate({...template, is_default: checked})} 
        />
        <Label>Set as default template</Label>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-4">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        )}
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  );
};

const CompanySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeTab, setActiveTab] = useState("templates");
  
  const getEmptyTemplate = () => ({
    name: "", 
    logo_url: "",
    company_name: "", 
    tagline: "",
    company_email: "",
    company_phone: "",
    company_website: "",
    company_address: "", 
    currency: "USD",
    timezone: "UTC",
    date_format: "MMM DD, YYYY",
    time_format: "12h",
    header_text: "", 
    footer_line1: "",
    footer_text: "",
    is_default: false
  });
  
  const [newTemplate, setNewTemplate] = useState(getEmptyTemplate());
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('auth_token');
    try {
      const [templatesRes, keyRes] = await Promise.all([
        axios.get(`${API}/company/templates?token=${token}`),
        axios.get(`${API}/company/api-key?token=${token}`)
      ]);
      setCompanyTemplates(templatesRes.data.error ? [] : templatesRes.data);
      setApiKey(keyRes.data.api_key || "");
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
    setLoading(false);
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.post(`${API}/company/templates?token=${token}`, newTemplate);
      if (response.data.success) {
        setNewTemplate(getEmptyTemplate());
        fetchData();
      } else {
        alert(response.data.error || "Failed to create template");
      }
    } catch (error) {
      alert("Error creating template");
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.put(`${API}/company/templates/${editingTemplate.id}?token=${token}`, editingTemplate);
      if (response.data.success) {
        setEditingTemplate(null);
        fetchData();
      } else {
        alert(response.data.error || "Failed to update template");
      }
    } catch (error) {
      alert("Error updating template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    const token = localStorage.getItem('auth_token');
    try {
      await axios.delete(`${API}/company/templates/${templateId}?token=${token}`);
      fetchData();
    } catch (error) {
      alert("Error deleting template");
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm("This will invalidate the current API key. Continue?")) return;
    const token = localStorage.getItem('auth_token');
    try {
      const response = await axios.post(`${API}/company/api-key/regenerate?token=${token}`);
      if (response.data.success) {
        setApiKey(response.data.api_key);
        alert("API key regenerated successfully");
      }
    } catch (error) {
      alert("Error regenerating key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Domain Administration</h1>
                <p className="text-sm text-gray-500">{user?.domain ? `Domain: ${user.domain}` : user?.email}</p>
              </div>
            </div>
            {user?.is_domain_admin && <Badge className="bg-blue-100 text-blue-800">Domain Admin</Badge>}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="templates">Company Templates</TabsTrigger>
            <TabsTrigger value="api">API Access</TabsTrigger>
          </TabsList>

          <TabsContent value="templates">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Create New Company Template</CardTitle>
                <CardDescription>Templates created here will be available to all users in your domain</CardDescription>
              </CardHeader>
              <CardContent>
                <CompanyTemplateForm template={newTemplate} setTemplate={setNewTemplate} onSubmit={handleCreateTemplate} submitLabel="Create Template" />
              </CardContent>
            </Card>

            <div className="space-y-4">
              {companyTemplates.map((template) => (
                <Card key={template.id}>
                  <CardContent className="p-6">
                    {editingTemplate?.id === template.id ? (
                      <div>
                        <h3 className="font-bold text-lg mb-4">Edit Template</h3>
                        <CompanyTemplateForm 
                          template={editingTemplate} 
                          setTemplate={setEditingTemplate} 
                          onSubmit={handleUpdateTemplate} 
                          submitLabel="Update" 
                          onCancel={() => setEditingTemplate(null)}
                        />
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          {template.logo_url && (
                            <img src={template.logo_url} alt="Logo" className="h-12 w-auto object-contain" />
                          )}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-lg">{template.name}</h3>
                              {template.is_default && <Badge className="bg-green-100 text-green-800">Default</Badge>}
                            </div>
                            <p className="text-sm font-medium text-gray-700">{template.company_name || "No company name"}</p>
                            {template.tagline && <p className="text-xs text-gray-500 italic">{template.tagline}</p>}
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                              {template.currency && <span>💰 {template.currency}</span>}
                              {template.timezone && <span>🕐 {template.timezone}</span>}
                              {template.date_format && <span>📅 {template.date_format}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingTemplate({...getEmptyTemplate(), ...template})}>
                            <Edit className="h-4 w-4 mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {companyTemplates.length === 0 && (
                <Card className="p-8 text-center text-gray-500">
                  <Building className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p>No company templates yet. Create one above to get started.</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="api">
            <Card>
              <CardHeader>
                <CardTitle>API Access</CardTitle>
                <CardDescription>Use this API key to programmatically generate login tokens for portal access</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Your API Key</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 bg-gray-100 px-4 py-2 rounded font-mono text-sm">
                      {showApiKey ? apiKey : "••••••••••••••••••••••••••••••••"}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(apiKey)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {(user?.is_domain_admin || !user?.domain) && (
                  <Button variant="outline" onClick={handleRegenerateKey}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Regenerate Key
                  </Button>
                )}

                <Separator />

                <div>
                  <h3 className="font-semibold mb-2">API Login Example</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST "${BACKEND_URL}/api/auth/api-login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "${apiKey ? apiKey.substring(0, 8) + '...' : 'YOUR_API_KEY'}",
    "email": "${user?.email || 'user@domain.com'}"
  }'

# Response:
{
  "success": true,
  "token": "abc123...",
  "portal_url": "/?token=abc123..."
}`}</pre>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ==================== APP ====================
// ==================== PROTECTED ROUTE WRAPPER ====================
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <LoginPage />;
  }
  
  return children;
};

// ==================== MAIN APP ====================
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <Routes>
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><SuperAdminPanel /></ProtectedRoute>} />
            
            {/* Strategy & OKRs Templates */}
            <Route path="/template/okr" element={<ProtectedRoute><OKRTemplateForm /></ProtectedRoute>} />
            <Route path="/template/kpi-dashboard" element={<ProtectedRoute><KPIDashboardForm /></ProtectedRoute>} />
            <Route path="/template/raci-matrix" element={<ProtectedRoute><RACIMatrixForm /></ProtectedRoute>} />
            <Route path="/template/strategic-roadmap" element={<ProtectedRoute><StrategicRoadmapForm /></ProtectedRoute>} />
            
            {/* Finance & Budgeting Templates */}
            <Route path="/template/budget-planning" element={<ProtectedRoute><BudgetPlanningForm /></ProtectedRoute>} />
            <Route path="/template/financial-forecast" element={<ProtectedRoute><FinancialForecastForm /></ProtectedRoute>} />
            <Route path="/template/roi-analysis" element={<ProtectedRoute><ROIAnalysisForm /></ProtectedRoute>} />
            <Route path="/template/vendor-comparison" element={<ProtectedRoute><VendorComparisonForm /></ProtectedRoute>} />
            
            {/* Resource & Capacity Templates */}
            <Route path="/template/capacity-planning" element={<ProtectedRoute><CapacityPlanningForm /></ProtectedRoute>} />
            <Route path="/template/resource-allocation" element={<ProtectedRoute><ResourceAllocationForm /></ProtectedRoute>} />
            <Route path="/template/hiring-plan" element={<ProtectedRoute><HiringPlanForm /></ProtectedRoute>} />
            <Route path="/template/skills-matrix" element={<ProtectedRoute><SkillsMatrixForm /></ProtectedRoute>} />
            
            {/* Engineering & DevOps Templates */}
            <Route path="/template/technical-design" element={<ProtectedRoute><TechnicalDesignForm /></ProtectedRoute>} />
            <Route path="/template/release-notes" element={<ProtectedRoute><ReleaseNotesForm /></ProtectedRoute>} />
            <Route path="/template/incident-report" element={<ProtectedRoute><IncidentReportForm /></ProtectedRoute>} />
            <Route path="/template/post-mortem" element={<ProtectedRoute><PostMortemForm /></ProtectedRoute>} />
            <Route path="/template/runbook" element={<ProtectedRoute><RunbookForm /></ProtectedRoute>} />
            <Route path="/template/api-documentation" element={<ProtectedRoute><APIDocumentationForm /></ProtectedRoute>} />
            
            {/* Executive Reports Templates */}
            <Route path="/template/executive-summary" element={<ProtectedRoute><ExecutiveSummaryForm /></ProtectedRoute>} />
            <Route path="/template/board-report" element={<ProtectedRoute><BoardReportForm /></ProtectedRoute>} />
            <Route path="/template/qbr" element={<ProtectedRoute><QBRForm /></ProtectedRoute>} />
            <Route path="/template/investor-update" element={<ProtectedRoute><InvestorUpdateForm /></ProtectedRoute>} />
            
            {/* Core Development Templates */}
            <Route path="/template/ba-requirements" element={<ProtectedRoute><BARequirementsTemplate /></ProtectedRoute>} />
            <Route path="/template/software-proposal" element={<ProtectedRoute><SoftwareProposalForm /></ProtectedRoute>} />
            <Route path="/template/product-tracksheet" element={<ProtectedRoute><ProductTrackSheetForm /></ProtectedRoute>} />
            <Route path="/template/cost-calculation" element={<ProtectedRoute><CostCalculationForm /></ProtectedRoute>} />
            <Route path="/template/bug-log" element={<ProtectedRoute><BugLogForm /></ProtectedRoute>} />
            <Route path="/template/test-cases" element={<ProtectedRoute><TestCasesForm /></ProtectedRoute>} />
            <Route path="/template/code-review" element={<ProtectedRoute><CodeReviewForm /></ProtectedRoute>} />
            
            {/* Meetings & Communication Templates */}
            <Route path="/template/minutes-of-meeting" element={<ProtectedRoute><MinutesOfMeetingTemplate /></ProtectedRoute>} />
            <Route path="/template/meeting-agenda" element={<ProtectedRoute><MeetingAgendaTemplate /></ProtectedRoute>} />
            <Route path="/template/action-item-tracker" element={<ProtectedRoute><ActionItemTrackerTemplate /></ProtectedRoute>} />
            <Route path="/template/stakeholder-communication-log" element={<ProtectedRoute><StakeholderCommunicationLogForm /></ProtectedRoute>} />
            
            {/* Scrum / Agile Templates */}
            <Route path="/template/sprint-planning" element={<ProtectedRoute><SprintPlanningTemplate /></ProtectedRoute>} />
            <Route path="/template/daily-standup" element={<ProtectedRoute><DailyStandupTemplate /></ProtectedRoute>} />
            <Route path="/template/sprint-retrospective" element={<ProtectedRoute><SprintRetrospectiveTemplate /></ProtectedRoute>} />
            <Route path="/template/product-backlog" element={<ProtectedRoute><ProductBacklogForm /></ProtectedRoute>} />
            <Route path="/template/burndown-chart" element={<ProtectedRoute><BurndownChartForm /></ProtectedRoute>} />
            <Route path="/template/definition-of-done" element={<ProtectedRoute><DefinitionOfDoneForm /></ProtectedRoute>} />
            <Route path="/template/definition-of-ready" element={<ProtectedRoute><DefinitionOfReadyForm /></ProtectedRoute>} />
            
            {/* Project Governance Templates */}
            <Route path="/template/change-request" element={<ProtectedRoute><ChangeRequestTemplate /></ProtectedRoute>} />
            <Route path="/template/decision-log" element={<ProtectedRoute><DecisionLogForm /></ProtectedRoute>} />
            <Route path="/template/issue-risk-register" element={<ProtectedRoute><IssueRiskRegisterForm /></ProtectedRoute>} />
            <Route path="/template/escalation-matrix" element={<ProtectedRoute><EscalationMatrixForm /></ProtectedRoute>} />
            
            {/* Delivery & Closure Templates */}
            <Route path="/template/go-live-checklist" element={<ProtectedRoute><GoLiveChecklistTemplate /></ProtectedRoute>} />
            <Route path="/template/project-closure" element={<ProtectedRoute><ProjectClosureForm /></ProtectedRoute>} />
            <Route path="/template/lessons-learned" element={<ProtectedRoute><LessonsLearnedForm /></ProtectedRoute>} />
            <Route path="/template/client-feedback" element={<ProtectedRoute><ClientFeedbackForm /></ProtectedRoute>} />
            
            {/* Saved Templates & API Docs */}
            <Route path="/saved-templates" element={<ProtectedRoute><SavedTemplatesPage /></ProtectedRoute>} />
            <Route path="/docs" element={<ProtectedRoute><APIDocsPage /></ProtectedRoute>} />
          </Routes>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
